/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  TextInput, Image, ActivityIndicator, Alert, Dimensions, FlatList,
  StatusBar, Share, Linking, Switch, Modal, Animated, PanResponder,
  Platform, Clipboard, ToastAndroid, KeyboardAvoidingView,
} from 'react-native';
import Slider from '@react-native-community/slider';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system';
import * as MediaLibrary from 'expo-media-library';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');
const P = '#0e4e96';
const Y = '#fdb913';
const S = '#1e7be2';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': API_KEY, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};

const CompanyLogo = ({ uri, size = 30 }) => uri
  ? <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 6 }} resizeMode="contain" />
  : <MaterialCommunityIcons name="office-building" size={size * 0.7} color="rgba(255,255,255,0.8)" />;

// ═══════════════════════════════════════════════════════════════════════════════
// DATA CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const DESIGN_STUDIOS = [
  { id:'canva',        name:'Canva',          icon:'palette',            color:'#7C3AED', gradient:['#7C3AED','#A78BFA'], free:true,  rating:4.8, reviews:'100M+ users',
    tagline:'Drag-drop design with 1M+ templates', features:['Social Media','Presentations','Logos','Business Cards','Posters'],
    webUrl:'https://www.canva.com/create/', imageUrl:'https://www.canva.com/create/social-media/' },
  { id:'nano_banana',  name:'Nano Banana',    icon:'robot',              color:'#FACC15', gradient:['#FACC15','#F59E0B'], free:true,  rating:4.5, reviews:'AI-powered',
    tagline:'AI-powered image & logo generation', features:['AI Images','Logo Maker','Brand Kit','SVG Export','Custom Colours'],
    webUrl:'https://nanobanana.io', imageUrl:'https://nanobanana.io/logo-maker' },
  { id:'adobe_express',name:'Adobe Express',  icon:'star-circle',        color:'#FF0000', gradient:['#FF0000','#FF6B35'], free:true,  rating:4.6, reviews:'Adobe ecosystem',
    tagline:'Adobe creative tools simplified', features:['Photo Edit','Templates','Brand Kit','Video','Logos'],
    webUrl:'https://new.express.adobe.com', imageUrl:'https://new.express.adobe.com/new' },
  { id:'figma',        name:'Figma',          icon:'draw-pen',           color:'#F24E1E', gradient:['#F24E1E','#FF7262'], free:true,  rating:4.7, reviews:'4M+ teams',
    tagline:'Professional vector & UI design', features:['Vector Graphics','Prototyping','Brand Systems','Plugins','Collaboration'],
    webUrl:'https://www.figma.com/' },
  { id:'picsart',      name:'Picsart',        icon:'brush',              color:'#FF3366', gradient:['#FF3366','#FF6B9D'], free:true,  rating:4.5, reviews:'150M+ users',
    tagline:'AI photo editing & creation', features:['AI Generate','Background Remove','Filters','Collage','Stickers'],
    webUrl:'https://picsart.com/create/', imageUrl:'https://picsart.com/ai-image-generator/' },
  { id:'midjourney',   name:'Midjourney',     icon:'image-filter-drama', color:'#0066FF', gradient:['#0066FF','#00CCFF'], free:false, rating:4.9, reviews:'Most powerful AI',
    tagline:'Hyper-realistic AI image generation', features:['Hyper-Realistic','Artistic Styles','4K Output','Variations','Upscale'],
    webUrl:'https://www.midjourney.com/', imageUrl:'https://www.midjourney.com/app/' },
  { id:'dalle',        name:'DALL·E 3',       icon:'creation',           color:'#10a37f', gradient:['#10a37f','#0e8a6b'], free:false, rating:4.8, reviews:'OpenAI',
    tagline:'OpenAI photorealistic image AI', features:['Photorealistic','Text in Images','Style Transfer','Inpainting','Edits'],
    webUrl:'https://openai.com/dall-e-3', imageUrl:'https://chat.openai.com' },
  { id:'looka',        name:'Looka',          icon:'star-shooting-outline',color:'#6366F1',gradient:['#6366F1','#8B5CF6'], free:false, rating:4.4, reviews:'20M+ logos',
    tagline:'AI brand identity & logo design', features:['AI Logo','Brand Kit','Business Cards','Social','Email Sigs'],
    webUrl:'https://looka.com', imageUrl:'https://looka.com/logo-maker/' },
  { id:'ideogram',     name:'Ideogram',       icon:'text-recognition',   color:'#EC4899', gradient:['#EC4899','#F9A8D4'], free:true,  rating:4.6, reviews:'Best text AI',
    tagline:'AI images with perfect text rendering', features:['Text in Images','Typography','Posters','Logos','Marketing'],
    webUrl:'https://ideogram.ai/', imageUrl:'https://ideogram.ai/t/explore' },
  { id:'leonardo',     name:'Leonardo AI',    icon:'lightning-bolt',     color:'#7C3AED', gradient:['#4F46E5','#7C3AED'], free:true,  rating:4.7, reviews:'Game & product art',
    tagline:'Game-quality AI art generation', features:['Game Assets','Product Images','Motion','Canvas','Fine-Tune'],
    webUrl:'https://leonardo.ai/', imageUrl:'https://app.leonardo.ai/' },
];

const AI_STYLES = [
  { id:'Photorealistic', icon:'camera',          desc:'True to life' },
  { id:'Artistic',       icon:'brush',            desc:'Painterly' },
  { id:'Corporate',      icon:'briefcase-outline',desc:'Professional' },
  { id:'Minimalist',     icon:'minus-circle-outline',desc:'Clean' },
  { id:'3D Render',      icon:'cube-outline',     desc:'Volumetric' },
  { id:'Blueprint',      icon:'floor-plan',       desc:'Technical' },
  { id:'Sketch',         icon:'pencil-outline',   desc:'Hand-drawn' },
  { id:'Cinematic',      icon:'movie-outline',    desc:'Film-quality' },
  { id:'Isometric',      icon:'view-comfy',       desc:'Geometric 3D' },
  { id:'Vintage',        icon:'image-filter-vintage', desc:'Retro look' },
  { id:'Watercolour',    icon:'water',            desc:'Soft art' },
  { id:'Neon',           icon:'lightning-bolt',   desc:'Glowing' },
  { id:'Cyberpunk',      icon:'robot-angry',      desc:'Futuristic' },
  { id:'Oil Painting',   icon:'palette-swatch',   desc:'Classic art' },
  { id:'Pixel Art',      icon:'grid',             desc:'8-bit style' },
  { id:'Line Art',       icon:'vector-line',      desc:'Outline' },
];

const RATIOS = [
  { id:'1:1',  label:'1:1',  desc:'Square',     dims:[512,512] },
  { id:'16:9', label:'16:9', desc:'Landscape',  dims:[1024,576] },
  { id:'9:16', label:'9:16', desc:'Story',      dims:[576,1024] },
  { id:'4:3',  label:'4:3',  desc:'Classic',    dims:[768,576] },
  { id:'3:4',  label:'3:4',  desc:'Portrait',   dims:[576,768] },
  { id:'2:1',  label:'2:1',  desc:'Panoramic',  dims:[1024,512] },
  { id:'21:9', label:'21:9', desc:'Ultra-wide', dims:[1260,540] },
  { id:'5:4',  label:'5:4',  desc:'Print',      dims:[640,512] },
  { id:'1:2',  label:'1:2',  desc:'Tall',       dims:[512,1024] },
];

const SOCIAL_FORMATS = [
  { id:'ig_post',    name:'Instagram Post',     dims:'1080×1080', ratio:'1:1',  icon:'instagram', color:'#E1306C' },
  { id:'ig_story',   name:'Instagram Story',    dims:'1080×1920', ratio:'9:16', icon:'instagram', color:'#E1306C' },
  { id:'ig_reel',    name:'Instagram Reel',     dims:'1080×1920', ratio:'9:16', icon:'instagram', color:'#E1306C' },
  { id:'li_post',    name:'LinkedIn Post',       dims:'1200×627',  ratio:'16:9', icon:'linkedin',  color:'#0077B5' },
  { id:'li_bg',      name:'LinkedIn Background', dims:'1584×396',  ratio:'4:1',  icon:'linkedin',  color:'#0077B5' },
  { id:'li_carousel',name:'LinkedIn Carousel',   dims:'1080×1080', ratio:'1:1',  icon:'linkedin',  color:'#0077B5' },
  { id:'tw_post',    name:'Twitter Post',        dims:'1200×675',  ratio:'16:9', icon:'twitter',   color:'#1DA1F2' },
  { id:'tw_header',  name:'Twitter Header',      dims:'1500×500',  ratio:'3:1',  icon:'twitter',   color:'#1DA1F2' },
  { id:'fb_post',    name:'Facebook Post',       dims:'1200×630',  ratio:'16:9', icon:'facebook',  color:'#1877F2' },
  { id:'fb_cover',   name:'Facebook Cover',      dims:'820×312',   ratio:'16:6', icon:'facebook',  color:'#1877F2' },
  { id:'fb_event',   name:'Facebook Event',      dims:'1920×1080', ratio:'16:9', icon:'facebook',  color:'#1877F2' },
  { id:'yt_thumb',   name:'YouTube Thumbnail',   dims:'1280×720',  ratio:'16:9', icon:'youtube',   color:'#FF0000' },
  { id:'yt_banner',  name:'YouTube Banner',      dims:'2560×1440', ratio:'16:9', icon:'youtube',   color:'#FF0000' },
  { id:'tk_video',   name:'TikTok Video',        dims:'1080×1920', ratio:'9:16', icon:'music-note',color:'#000000' },
  { id:'pin_standard',name:'Pinterest Standard',  dims:'1000×1500', ratio:'2:3',  icon:'pinterest', color:'#E60023' },
  { id:'email_band', name:'Email Banner',        dims:'600×200',   ratio:'3:1',  icon:'email',     color:P },
  { id:'job_banner', name:'Job Board Banner',    dims:'1200×400',  ratio:'3:1',  icon:'briefcase', color:'#28a745' },
  { id:'web_hero',   name:'Website Hero',        dims:'1920×800',  ratio:'12:5', icon:'web',       color:'#6366F1' },
  { id:'ad_banner',  name:'Display Ad',          dims:'728×90',    ratio:'8:1',  icon:'advertisement',color:'#F59E0B' },
];

const PROMPT_CATEGORIES = [
  { label:'Engineering', icon:'hard-hat', prompts:[
    'Modern civil engineering construction site with cranes and steel, golden hour',
    'Diverse engineering team reviewing technical blueprints in a glass-walled office',
    'Futuristic sustainable architecture with green roof and solar panels, UK cityscape',
    'Precision manufacturing facility with robotic assembly lines, clean aesthetic',
    'Underground tunnel construction project with advanced boring machinery',
    'Bridge engineering marvel at sunset, suspension cables glowing',
    'Renewable energy wind farm on rolling green hills, aerial view',
    'Smart city infrastructure with IoT sensors and digital overlays',
  ]},
  { label:'Recruitment', icon:'account-group', prompts:[
    'Professional job interview in a modern glass-walled boardroom, confident candidate',
    'Diverse team of professionals celebrating project success in a modern office',
    'Graduate engineer confident at a construction site wearing safety equipment',
    'Networking event at an engineering industry conference with engaged professionals',
    'Career growth concept: ladder of opportunity in a modern UK city',
    'Remote work setup: professional video call with engineering team',
    'Job fair booth with company branding and enthusiastic recruiters',
    'Onboarding session: new employee welcomed by diverse team',
  ]},
  { label:'Brand', icon:'bullhorn-outline', prompts:[
    'JobEng brand hero: connected professionals across UK cities, vibrant colours',
    'Split screen: construction site left, digital HR dashboard right',
    'Professional UK engineering landscape at golden hour, inspiring',
    'Abstract network of talent and opportunity, blue tones',
    'Company values visualization: integrity, innovation, inclusion',
    'Employer branding: diverse team photo with modern office backdrop',
    'Industry leadership: podium speaker at engineering summit',
    'Community impact: engineering project benefiting local neighborhood',
  ]},
  { label:'Marketing', icon:'chart-line', prompts:[
    'Data visualization dashboard with growth charts and metrics, blue gradient',
    'Social media engagement concept: likes, shares, comments floating',
    'Email marketing campaign visual: inbox with compelling newsletter',
    'Content marketing: blog post with engaging featured image',
    'SEO success: website ranking at top of search results',
    'Performance marketing: ROI calculator with positive returns',
    'Event marketing: conference stage with packed audience',
    'Product launch: new engineering software interface reveal',
  ]},
];

const MOOD_COLORS = ['#0e4e96','#1e7be2','#fdb913','#ef4444','#10b981','#8b5cf6','#f97316','#06b6d4','#f43f5e','#84cc16','#000','#fff','#6366f1','#ec4899','#14b8a6'];

const FILTERS = [
  { id:'normal',   name:'Normal',    icon:'image' },
  { id:'vivid',    name:'Vivid',     icon:'brightness-5' },
  { id:'warm',     name:'Warm',      icon:'white-balance-sunny' },
  { id:'cool',     name:'Cool',      icon:'snowflake' },
  { id:'mono',     name:'B&W',       icon:'image-filter-black-white' },
  { id:'sepia',    name:'Sepia',     icon:'image-filter-vintage' },
  { id:'fade',     name:'Fade',      icon:'image-filter-hdr' },
  { id:'noir',     name:'Noir',      icon:'image-filter-frames' },
  { id:'chrome',   name:'Chrome',    icon:'image-filter-center-focus' },
  { id:'process',  name:'Process',   icon:'image-filter-drama' },
  { id:'transfer', name:'Transfer',  icon:'image-filter-tilt-shift' },
  { id:'instant',  name:'Instant',   icon:'camera-iris' },
];

const MOCKUP_TYPES = [
  { id:'iphone',      name:'iPhone 15',      icon:'cellphone',       frame:'https://cdn.dribbble.com/assets/iphone-15-frame.png' },
  { id:'macbook',     name:'MacBook Pro',    icon:'laptop',          frame:'https://cdn.dribbble.com/assets/macbook-frame.png' },
  { id:'billboard',   name:'Billboard',      icon:'billboard',       frame:'https://cdn.dribbble.com/assets/billboard-frame.png' },
  { id:'business_card',name:'Business Card',  icon:'card-account-details', frame:'https://cdn.dribbble.com/assets/card-frame.png' },
  { id:'tshirt',      name:'T-Shirt',        icon:'tshirt-crew',     frame:'https://cdn.dribbble.com/assets/tshirt-frame.png' },
  { id:'mug',         name:'Coffee Mug',     icon:'coffee',          frame:'https://cdn.dribbble.com/assets/mug-frame.png' },
  { id:'tablet',      name:'iPad Pro',       icon:'tablet',          frame:'https://cdn.dribbble.com/assets/ipad-frame.png' },
  { id:'watch',       name:'Smart Watch',    icon:'watch',           frame:'https://cdn.dribbble.com/assets/watch-frame.png' },
];

const TABS = [
  { id:'create',    label:'AI Create',    icon:'robot-outline' },
  { id:'social',    label:'Social',       icon:'share-variant-outline' },
  { id:'edit',      label:'Edit & Tools', icon:'image-edit-outline' },
  { id:'mockups',   label:'Mockups',      icon:'cube-outline' },
  { id:'templates', label:'Templates',    icon:'view-grid-outline' },
  { id:'gallery',   label:'Gallery',      icon:'image-multiple-outline' },
  { id:'brand',     label:'Brand Kit',    icon:'palette-outline' },
  { id:'studios',   label:'AI Studios',   icon:'storefront-outline' },
];

const FOLDER_PRESETS = [
  { id:'all',       name:'All Images',     icon:'image-multiple', color:P },
  { id:'favorites', name:'Favorites',      icon:'heart',          color:'#ef4444' },
  { id:'campaigns', name:'Campaigns',      icon:'bullhorn',       color:'#8b5cf6' },
  { id:'social',    name:'Social Media',   icon:'share-variant',  color:'#ec4899' },
  { id:'logos',     name:'Logos & Brand',  icon:'star-circle',    color:'#f59e0b' },
  { id:'team',      name:'Team Photos',    icon:'account-group',  color:'#10b981' },
  { id:'projects',  name:'Project Images', icon:'hard-hat',       color:'#6366f1' },
  { id:'marketing', name:'Marketing',      icon:'chart-line',     color:'#06b6d4' },
  { id:'archived',  name:'Archived',       icon:'archive',        color:'#64748b' },
];

const WATERMARK_POSITIONS = [
  { id:'top-left',     label:'Top Left',     icon:'arrow-top-left' },
  { id:'top-right',    label:'Top Right',    icon:'arrow-top-right' },
  { id:'bottom-left',  label:'Bottom Left',  icon:'arrow-bottom-left' },
  { id:'bottom-right', label:'Bottom Right', icon:'arrow-bottom-right' },
  { id:'center',       label:'Center',       icon:'crosshairs' },
  { id:'tile',         label:'Tiled',        icon:'view-grid' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ImageCreatorScreen({ navigation, route }) {
  const campaignId = route?.params?.campaignId || null;
  const campaign   = route?.params?.campaign   || null;

  // ─── Company & Auth ─────────────────────────────────────────────────────────
  const [companyLogo,     setCompanyLogo]     = useState(null);
  const [businessName,    setBusinessName]    = useState('');
  const [companyName,     setCompanyName]     = useState('');
  const [userRole,        setUserRole]        = useState('editor');
  const [teamMembers,     setTeamMembers]     = useState([]);

  // ─── Tabs & Navigation ────────────────────────────────────────────────────
  const [activeTab,       setActiveTab]       = useState('create');
  const [prevTab,         setPrevTab]         = useState('create');

  // ─── Brand Settings ─────────────────────────────────────────────────────────
  const [brandColors,     setBrandColors]     = useState([P, '#F5A623', '#10B981', '#EF4444', '#8B5CF6', '#06b6d4']);
  const [brandFonts,      setBrandFonts]      = useState(['DM Sans', 'Cormorant Garamond', 'Inter', 'Poppins', 'Montserrat']);
  const [selectedBrandColor, setSelectedBrandColor] = useState(P);
  const [brandVoice,      setBrandVoice]      = useState('professional');
  const [brandGuidelines, setBrandGuidelines] = useState('');

  // ─── Create Tab ─────────────────────────────────────────────────────────────
  const [prompt,          setPrompt]          = useState('');
  const [negativePrompt,  setNegativePrompt]  = useState('blurry, low quality, distorted, watermark, text, deformed hands');
  const [selectedStyle,   setSelectedStyle]   = useState('Photorealistic');
  const [selectedRatio,   setSelectedRatio]   = useState('16:9');
  const [accentColor,     setAccentColor]     = useState(P);
  const [isGenerating,    setIsGenerating]    = useState(false);
  const [generatedImage,  setGeneratedImage]  = useState(null);
  const [variations,      setVariations]      = useState([]);
  const [numVariations,   setNumVariations]   = useState(1);
  const [showAdvanced,    setShowAdvanced]    = useState(false);
  const [includeText,     setIncludeText]     = useState(false);
  const [overlayText,     setOverlayText]     = useState('');
  const [highQuality,     setHighQuality]     = useState(true);
  const [aiEnhance,       setAiEnhance]       = useState(true);
  const [seedValue,       setSeedValue]       = useState('');
  const [guidanceScale,   setGuidanceScale]   = useState(7.5);
  const [inferenceSteps,  setInferenceSteps]  = useState(30);
  const [batchMode,       setBatchMode]       = useState(false);
  const [batchPrompts,    setBatchPrompts]    = useState('');
  const [batchResults,    setBatchResults]    = useState([]);
  const [batchProgress,   setBatchProgress]   = useState(0);

  // ─── Social Tab ─────────────────────────────────────────────────────────────
  const [selectedSocial,  setSelectedSocial]  = useState(SOCIAL_FORMATS[0]);
  const [socialPrompt,    setSocialPrompt]    = useState('');
  const [socialGen,       setSocialGen]       = useState(false);
  const [socialCaption,   setSocialCaption]   = useState('');
  const [hashtags,        setHashtags]        = useState('');
  const [schedulePost,    setSchedulePost]    = useState(false);
  const [scheduleDate,    setScheduleDate]    = useState('');

  // ─── Edit & Tools Tab ───────────────────────────────────────────────────────
  const [uploadedImage,   setUploadedImage]   = useState(null);
  const [editPrompt,      setEditPrompt]      = useState('');
  const [editing,         setEditing]         = useState(false);
  const [bgRemoving,      setBgRemoving]      = useState(false);
  const [bgRemovedImage,  setBgRemovedImage]  = useState(null);
  const [upscaling,       setUpscaling]       = useState(false);
  const [colorizing,      setColorizing]      = useState(false);
  const [currentFilter,   setCurrentFilter]   = useState('normal');
  const [brightness,      setBrightness]      = useState(0);
  const [contrast,        setContrast]        = useState(0);
  const [saturation,      setSaturation]      = useState(0);
  const [blurAmount,      setBlurAmount]      = useState(0);
  const [vignette,        setVignette]        = useState(0);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [rotation,        setRotation]        = useState(0);
  const [flipH,           setFlipH]           = useState(false);
  const [flipV,           setFlipV]           = useState(false);
  const [cropMode,        setCropMode]        = useState(false);
  const [editHistory,     setEditHistory]     = useState([]);
  const [historyIndex,    setHistoryIndex]    = useState(-1);
  const [showHistory,     setShowHistory]     = useState(false);

  // ─── Mockups Tab ──────────────────────────────────────────────────────────
  const [selectedMockup,  setSelectedMockup]  = useState(MOCKUP_TYPES[0]);
  const [mockupImage,     setMockupImage]     = useState(null);
  const [mockupGenerating,setMockupGenerating]= useState(false);
  const [mockupColor,     setMockupColor]     = useState('#ffffff');

  // ─── Gallery Tab ────────────────────────────────────────────────────────────
  const [gallery,         setGallery]         = useState([]);
  const [galleryLoading,  setGalleryLoading]  = useState(false);
  const [selectedItems,   setSelectedItems]   = useState([]);
  const [multiSelect,     setMultiSelect]     = useState(false);
  const [gallerySearch,   setGallerySearch]   = useState('');
  const [galleryFilter,   setGalleryFilter]   = useState('All');
  const [gallerySortBy,   setGallerySortBy]   = useState('newest');
  const [galleryViewMode, setGalleryViewMode] = useState('grid');
  const [activeFolder,    setActiveFolder]    = useState('all');
  const [favorites,       setFavorites]       = useState([]);
  const [imageTags,       setImageTags]       = useState({});
  const [showTagEditor,   setShowTagEditor]   = useState(false);
  const [tagInput,        setTagInput]        = useState('');
  const [activeImageId,   setActiveImageId]   = useState(null);
  const [folders,         setFolders]         = useState(FOLDER_PRESETS);
  const [customFolders,   setCustomFolders]   = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName,   setNewFolderName]   = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [styleFilter,     setStyleFilter]     = useState('all');
  const [showImageDetail, setShowImageDetail] = useState(false);
  const [detailImage,     setDetailImage]     = useState(null);
  const [compareMode,     setCompareMode]     = useState(false);
  const [compareImages,   setCompareImages]   = useState([]);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowIndex,  setSlideshowIndex]  = useState(0);

  // ─── Templates Tab ──────────────────────────────────────────────────────────
  const [savedTemplates,  setSavedTemplates]  = useState([]);
  const [showTemplateSave, setShowTemplateSave] = useState(false);
  const [templateName,    setTemplateName]    = useState('');

  // ─── Brand Kit Tab ──────────────────────────────────────────────────────────
  const [watermark,       setWatermark]       = useState(false);
  const [watermarkSettings, setWatermarkSettings] = useState({
    text: '', position: 'bottom-right', opacity: 0.6, size: 24, font: 'Inter', color: '#ffffff'
  });
  const [showWatermarkDesigner, setShowWatermarkDesigner] = useState(false);
  const [imageFormat,     setImageFormat]     = useState('png');
  const [imageQuality,    setImageQuality]    = useState('high');
  const [exportDpi,       setExportDpi]       = useState(72);
  const [printMode,       setPrintMode]       = useState(false);
  const [cmykMode,        setCmykMode]        = useState(false);
  const [bleedSize,       setBleedSize]       = useState(0);
  const [history,         setHistory]         = useState([]);
  const [promptLibrary,   setPromptLibrary]   = useState([]);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [promptLibSearch, setPromptLibSearch] = useState('');
  const [promptCategory,  setPromptCategory]  = useState('all');
  const [colorPalette,    setColorPalette]    = useState([]);
  const [showColorExtractor, setShowColorExtractor] = useState(false);
  const [extractingColors, setExtractingColors] = useState(false);

  // ─── Studios Tab ────────────────────────────────────────────────────────────
  const [studioLaunching, setStudioLaunching] = useState(null);
  const [studioFilter,    setStudioFilter]    = useState('All');

  // ─── AI & Collaboration ───────────────────────────────────────────────────
  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('ImageCreatorScreen');
  const [showAIChat,      setShowAIChat]      = useState(false);
  const [aiAltText,       setAiAltText]       = useState('');
  const [generatingAltText, setGeneratingAltText] = useState(false);
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
  const [accessibilityReport, setAccessibilityReport] = useState(null);
  const [comments,        setComments]        = useState({});
  const [showComments,    setShowComments]    = useState(false);
  const [commentInput,    setCommentInput]    = useState('');
  const [imageStats,      setImageStats]      = useState({});
  const [showStats,       setShowStats]       = useState(false);

  // ─── Export & Scheduling ────────────────────────────────────────────────────
  const [scheduledExports, setScheduledExports] = useState([]);
  const [showScheduler,    setShowScheduler]    = useState(false);
  const [scheduleTitle,    setScheduleTitle]    = useState('');
  const [schedulePlatform, setSchedulePlatform] = useState('instagram');
  const [exportProgress,   setExportProgress]   = useState(null);
  const [isExporting,      setIsExporting]      = useState(false);

  // ─── Offline & Sync ───────────────────────────────────────────────────────
  const [isOfflineMode,   setIsOfflineMode]   = useState(false);
  const [offlineQueue,    setOfflineQueue]     = useState([]);
  const [syncStatus,      setSyncStatus]       = useState('synced');

  // ─── UI Animations ──────────────────────────────────────────────────────────
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  // ═══════════════════════════════════════════════════════════════════════════════
  // INIT & PERSISTENCE
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    loadAllData();
    checkNetworkStatus();
    const netInterval = setInterval(checkNetworkStatus, 30000);
    return () => clearInterval(netInterval);
  }, []);

  const loadAllData = async () => {
    try {
      const raw = await AsyncStorage.getItem('company_profile').catch(() => null);
      if (raw) {
        const p = JSON.parse(raw);
        const logo = p.logo || p.logo_url || p.company_logo;
        if (logo) setCompanyLogo(logo.startsWith('http') ? logo : `${API_BASE}${logo}`);
        setBusinessName(p.company_name || p.name || '');
        setCompanyName(p.company_name || p.name || '');
        setBrandVoice(p.brand_voice || 'professional');
        setBrandGuidelines(p.brand_guidelines || '');
      }
      const role = await AsyncStorage.getItem('user_role').catch(() => 'editor');
      setUserRole(role || 'editor');
      await loadGallery();
      const favs = await AsyncStorage.getItem('image_favorites').catch(() => '[]');
      setFavorites(JSON.parse(favs || '[]'));
      const tags = await AsyncStorage.getItem('image_tags').catch(() => '{}');
      setImageTags(JSON.parse(tags || '{}'));
      const hist = await AsyncStorage.getItem('image_gen_history').catch(() => '[]');
      setHistory(JSON.parse(hist || '[]'));
      const lib = await AsyncStorage.getItem('prompt_library').catch(() => '[]');
      setPromptLibrary(JSON.parse(lib || '[]'));
      const temps = await AsyncStorage.getItem('saved_templates').catch(() => '[]');
      setSavedTemplates(JSON.parse(temps || '[]'));
      const wm = await AsyncStorage.getItem('watermark_settings').catch(() => null);
      if (wm) setWatermarkSettings(JSON.parse(wm));
      const sched = await AsyncStorage.getItem('scheduled_exports').catch(() => '[]');
      setScheduledExports(JSON.parse(sched || '[]'));
      const cf = await AsyncStorage.getItem('custom_folders').catch(() => '[]');
      setCustomFolders(JSON.parse(cf || '[]'));
      const comm = await AsyncStorage.getItem('image_comments').catch(() => '{}');
      setComments(JSON.parse(comm || '{}'));
      const stats = await AsyncStorage.getItem('image_stats').catch(() => '{}');
      setImageStats(JSON.parse(stats || '{}'));
    } catch (e) { console.log('Init load error:', e); }
  };

  const checkNetworkStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`, { method:'HEAD', timeout:5000 }).catch(() => null);
      setIsOfflineMode(!response);
      if (response && offlineQueue.length > 0) processOfflineQueue();
    } catch { setIsOfflineMode(true); }
  };

  const processOfflineQueue = async () => {
    setSyncStatus('syncing');
    for (const item of offlineQueue) {
      try {
        if (item.type === 'save_gallery') await saveToGallery(item.data);
        if (item.type === 'delete') await performDelete(item.id);
      } catch (e) { console.log('Sync error:', e); }
    }
    setOfflineQueue([]);
    await AsyncStorage.setItem('offline_queue', '[]');
    setSyncStatus('synced');
    loadGallery();
  };

  const addToOfflineQueue = (action) => {
    const updated = [...offlineQueue, action];
    setOfflineQueue(updated);
    AsyncStorage.setItem('offline_queue', JSON.stringify(updated));
    setSyncStatus('pending');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // GALLERY API
  // ═══════════════════════════════════════════════════════════════════════════════

  const loadGallery = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const h = await getH();
      const bid = await AsyncStorage.getItem('company_id').catch(() => '0');
      const res = await fetch(`${API_BASE}/api/data/design_assets?company_id=${parseInt(bid)||0}&type=image&limit=200`, { headers: h });
      const json = await res.json().catch(() => ({}));
      const data = Array.isArray(json?.data) ? json.data : [];
      if (data.length) {
        setGallery(data);
        const tagMap = {};
        data.forEach(item => { if (item.tags) tagMap[item.id] = item.tags; });
        setImageTags(prev => ({...prev, ...tagMap}));
      } else {
        const cached = await AsyncStorage.getItem(`img_gallery_${bid}`).catch(() => null);
        if (cached) setGallery(JSON.parse(cached));
      }
    } catch {}
    finally { setGalleryLoading(false); }
  }, []);

  const saveToGallery = async (imgData, folder = 'all') => {
    try {
      const h = await getH();
      const bid = await AsyncStorage.getItem('company_id').catch(() => '0');
      const payload = {
        company_id: parseInt(bid)||0, campaign_id: campaignId||null, type: 'image',
        name: (imgData.prompt || 'Generated Image').slice(0,80), file_url: imgData.url,
        folder: folder, tags: imgData.tags || [],
        data: JSON.stringify({...imgData, folder}),
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      };
      if (isOfflineMode) {
        addToOfflineQueue({ type:'save_gallery', data: payload });
        const saved = { ...payload, id: `offline_${Date.now()}_${Math.random().toString(36).slice(2,9)}` };
        setGallery(prev => [saved, ...prev]);
        return saved;
      }
      const res = await fetch(`${API_BASE}/api/data/design_assets`, { method:'POST', headers:h, body:JSON.stringify(payload) });
      const json = await res.json().catch(() => ({}));
      const saved = json?.data || { ...payload, id:`img_${Date.now()}` };
      setGallery(prev => [saved, ...prev]);
      const key = `img_gallery_${bid}`;
      const arr = JSON.parse(await AsyncStorage.getItem(key).catch(()=>'[]')||'[]');
      await AsyncStorage.setItem(key, JSON.stringify([saved,...arr].slice(0,200)));
      const newStats = {...imageStats, [saved.id]: { views:0, downloads:0, shares:0, created: new Date().toISOString() }};
      setImageStats(newStats);
      await AsyncStorage.setItem('image_stats', JSON.stringify(newStats));
      return saved;
    } catch (e) {
      if (isOfflineMode) addToOfflineQueue({ type:'save_gallery', data: imgData });
      throw e;
    }
  };

  const deleteItem = async (id, confirm = true) => {
    if (confirm) {
      Alert.alert('Delete Image', 'Remove from gallery? This cannot be undone.', [
        { text:'Cancel', style:'cancel' },
        { text:'Delete', style:'destructive', onPress: () => performDelete(id) },
      ]);
    } else { performDelete(id); }
  };

  const performDelete = async (id) => {
    try {
      if (String(id).startsWith('offline_')) {
        setGallery(prev => prev.filter(i => i.id !== id));
        setSelectedItems(prev => prev.filter(x => x !== id));
        return;
      }
      const h = await getH();
      fetch(`${API_BASE}/api/data/design_assets/${id}`, { method:'DELETE', headers:h }).catch(()=>{});
      setGallery(prev => prev.filter(i => i.id !== id));
      setSelectedItems(prev => prev.filter(x => x !== id));
      setFavorites(prev => prev.filter(x => x !== id));
      const newTags = {...imageTags}; delete newTags[id];
      setImageTags(newTags);
      await AsyncStorage.setItem('image_tags', JSON.stringify(newTags));
    } catch {}
  };

  const batchDelete = () => {
    Alert.alert(`Delete ${selectedItems.length} Images?`, 'This will permanently remove all selected images.', [
      { text:'Cancel', style:'cancel' },
      { text:'Delete All', style:'destructive', onPress: () => {
        selectedItems.forEach(id => performDelete(id));
        setSelectedItems([]); setMultiSelect(false);
      }},
    ]);
  };

  const toggleFavorite = async (id) => {
    const updated = favorites.includes(id) ? favorites.filter(x => x !== id) : [...favorites, id];
    setFavorites(updated);
    await AsyncStorage.setItem('image_favorites', JSON.stringify(updated));
  };

  const addTag = async (imageId, tag) => {
    if (!tag.trim()) return;
    const updated = { ...imageTags, [imageId]: [...(imageTags[imageId]||[]), tag.trim()] };
    setImageTags(updated);
    await AsyncStorage.setItem('image_tags', JSON.stringify(updated));
  };

  const removeTag = async (imageId, tag) => {
    const updated = { ...imageTags, [imageId]: (imageTags[imageId]||[]).filter(t => t !== tag) };
    setImageTags(updated);
    await AsyncStorage.setItem('image_tags', JSON.stringify(updated));
  };

  const moveToFolder = async (imageIds, folderId) => {
    const updated = gallery.map(item => imageIds.includes(item.id) ? {...item, folder: folderId} : item);
    setGallery(updated);
    setSelectedItems([]); setMultiSelect(false);
    try {
      const h = await getH();
      for (const id of imageIds) {
        fetch(`${API_BASE}/api/data/design_assets/${id}`, { method:'PATCH', headers:h, body:JSON.stringify({folder:folderId}) }).catch(()=>{});
      }
    } catch {}
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    const folder = { id: `folder_${Date.now()}`, name: newFolderName.trim(), icon: 'folder', color: '#6366f1' };
    const updated = [...customFolders, folder];
    setCustomFolders(updated);
    await AsyncStorage.setItem('custom_folders', JSON.stringify(updated));
    setNewFolderName(''); setShowFolderModal(false);
  };

  const duplicateImage = async (item) => {
    try {
      const duplicated = { ...item, id: `img_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, name: `${item.name} (Copy)`, created_at: new Date().toISOString() };
      setGallery(prev => [duplicated, ...prev]);
      await saveToGallery({ url: item.file_url, prompt: item.name, tags: imageTags[item.id] || [] });
      Alert.alert('✅ Duplicated', 'Image copied to gallery');
    } catch (e) { Alert.alert('Error', e.message); }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // AI GENERATION ENGINE
  // ═══════════════════════════════════════════════════════════════════════════════

  const buildPrompt = (base, opts = {}) => {
    let p = base.trim();
    const bn = opts.businessName || businessName;
    if (bn) p += `, for ${bn}, UK company`;
    if (opts.includeText && opts.overlayText) p += `, with bold text overlay: "${opts.overlayText}"`;
    if (opts.selectedStyle) p += `, ${opts.selectedStyle} style`;
    if (opts.highQuality !== false) p += ', ultra HD, sharp details, professional photography, 8k resolution';
    if (opts.accentColor && opts.accentColor !== P) p += `, dominant colour ${opts.accentColor}, colour harmony`;
    if (opts.brandVoice === 'professional') p += ', corporate professional aesthetic';
    if (opts.brandVoice === 'playful') p += ', vibrant energetic playful style';
    if (opts.brandVoice === 'luxury') p += ', premium luxury elegant aesthetic';
    if (opts.negativePrompt) p += ` | avoid: ${opts.negativePrompt}`;
    return p;
  };

  const enhancePromptWithAI = async (basePrompt, style) => {
    try {
      const aiRes = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'anthropic-version':'2023-06-01', 'x-api-key': 'YOUR_ANTHROPIC_KEY' },
        body: JSON.stringify({
          model:'claude-sonnet-4-20250514', max_tokens:300,
          system:'You are an expert AI image prompt engineer specializing in commercial and marketing imagery. Enhance prompts to be more detailed, vivid, and commercially effective. Return ONLY the enhanced prompt text.',
          messages:[{ role:'user', content:`Enhance for ${style} style commercial image: "${basePrompt}"` }],
        }),
      });
      const aiJson = await aiRes.json();
      return aiJson?.content?.[0]?.text || basePrompt;
    } catch { return basePrompt; }
  };

  const generateImageCore = async (inputPrompt, options = {}) => {
    const opts = { selectedStyle, highQuality, accentColor, businessName, negativePrompt, brandVoice, ...options };
    let finalPrompt = buildPrompt(inputPrompt, opts);
    if (aiEnhance && !options.skipEnhance) {
      finalPrompt = await enhancePromptWithAI(finalPrompt, opts.selectedStyle);
    }
    let urls = [];
    const variationCount = options.numVariations || numVariations;

    // Backend 1: Picsart
    try {
      const picsRes = await fetch('https://genai-api.picsart.io/v1/text2image', {
        method:'POST',
        headers:{ 'X-Picsart-API-Key':'YOUR_PICSART_KEY', 'Content-Type':'application/json' },
        body: JSON.stringify({
          prompt: finalPrompt, negative_prompt: negativePrompt,
          width: 512, height: 512, count: variationCount,
          guidance_scale: guidanceScale, num_inference_steps: inferenceSteps,
        }),
      });
      const picsJson = await picsRes.json().catch(()=>({}));
      if (picsJson?.data) {
        urls = Array.isArray(picsJson.data) ? picsJson.data.map(d=>d.url).filter(Boolean) : [picsJson.data?.url].filter(Boolean);
      }
    } catch {}

    // Backend 2: Stability AI
    if (!urls.length) {
      try {
        const stabRes = await fetch('https://api.stability.ai/v2beta/stable-image/generate/sd3', {
          method:'POST',
          headers:{ Authorization:'Bearer YOUR_STABILITY_KEY', 'Content-Type':'application/json' },
          body: JSON.stringify({ prompt: finalPrompt, output_format:'png', aspect_ratio: selectedRatio.replace(':','/') }),
        });
        const stabJson = await stabRes.json();
        if (stabJson?.image) urls = [`data:image/png;base64,${stabJson.image}`];
      } catch {}
    }

    // Fallback: DiceBear
    if (!urls.length) {
      const styleMap = {
        Photorealistic:'shapes', Artistic:'rings', Corporate:'identicon', Minimalist:'shapes',
        '3D Render':'bottts', Blueprint:'pixel-art', Sketch:'thumbs', Cinematic:'shapes',
        Isometric:'shapes', Vintage:'rings', Watercolour:'rings', Neon:'shapes',
        Cyberpunk:'bottts', 'Oil Painting':'rings', 'Pixel Art':'pixel-art', 'Line Art':'identicon',
      };
      const ds = styleMap[opts.selectedStyle]||'shapes';
      const bg = (opts.accentColor || P).replace('#','');
      for (let i=0; i<variationCount; i++) {
        const seed = encodeURIComponent(finalPrompt.slice(0,30)+`_${i}_${Date.now()}`);
        urls.push(`https://api.dicebear.com/8.x/${ds}/png?seed=${seed}&size=512&backgroundColor=${bg}`);
      }
    }
    return { urls, prompt: finalPrompt, originalPrompt: inputPrompt };
  };

  const handleGenerateImage = async () => {
    if (!prompt.trim()) { Alert.alert('Required', 'Enter a prompt to generate an image'); return; }
    setIsGenerating(true); setVariations([]); setBatchResults([]);
    try {
      const result = await generateImageCore(prompt);
      setGeneratedImage(result.urls[0]);
      setVariations(result.urls);
      setActiveTab('edit');
      const histItem = { id: `hist_${Date.now()}`, prompt, style: selectedStyle, ratio: selectedRatio, timestamp: new Date().toISOString() };
      const newHistory = [histItem, ...history].slice(0,50);
      setHistory(newHistory);
      await AsyncStorage.setItem('image_gen_history', JSON.stringify(newHistory));
      await saveToGallery({ url: result.urls[0], prompt, style: selectedStyle, ratio: selectedRatio, variations: result.urls, tags: [selectedStyle, 'ai-generated'] });
      if (campaignId) {
        const key = `campaign_content_${campaignId}`;
        const arr = JSON.parse(await AsyncStorage.getItem(key).catch(()=>'[]')||'[]');
        arr.unshift({ type:'image', title:prompt.slice(0,60), url:result.urls[0], created_at:new Date().toISOString(), id:`img_${Date.now()}` });
        await AsyncStorage.setItem(key, JSON.stringify(arr.slice(0,50)));
      }
    } catch(e) { Alert.alert('Generation failed', e.message); }
    finally { setIsGenerating(false); }
  };

  const handleBatchGenerate = async () => {
    if (!batchPrompts.trim()) { Alert.alert('Required', 'Enter at least one prompt for batch generation'); return; }
    const prompts = batchPrompts.split('\n').filter(p => p.trim());
    if (!prompts.length) { Alert.alert('Required', 'Enter valid prompts (one per line)'); return; }
    setIsGenerating(true); setBatchProgress(0); setBatchResults([]);
    const results = [];
    for (let i = 0; i < prompts.length; i++) {
      try {
        setBatchProgress(Math.round(((i) / prompts.length) * 100));
        const result = await generateImageCore(prompts[i], { skipEnhance: true, numVariations: 1 });
        results.push({ prompt: prompts[i], url: result.urls[0], id: `batch_${Date.now()}_${i}` });
        await saveToGallery({ url: result.urls[0], prompt: prompts[i], tags: ['batch', selectedStyle] });
      } catch (e) { results.push({ prompt: prompts[i], error: e.message, id: `batch_err_${Date.now()}_${i}` }); }
    }
    setBatchResults(results);
    setBatchProgress(100);
    setIsGenerating(false);
    setActiveTab('gallery');
    Alert.alert('Batch Complete', `Generated ${results.filter(r => r.url).length}/${prompts.length} images`);
  };

  const generateSocialImage = async () => {
    const p = socialPrompt.trim() || `Create a ${selectedSocial.name} for ${businessName||'my brand'}`;
    setSocialGen(true);
    try {
      const result = await generateImageCore(p, { selectedStyle: 'Corporate', numVariations: 1 });
      setGeneratedImage(result.urls[0]);
      if (aiEnhance) {
        try {
          const capRes = await fetch('https://api.anthropic.com/v1/messages', {
            method:'POST',
            headers:{ 'Content-Type':'application/json', 'anthropic-version':'2023-06-01' },
            body: JSON.stringify({
              model:'claude-sonnet-4-20250514', max_tokens:150,
              system:'Write engaging social media captions and hashtags for engineering/recruitment companies. Return JSON with caption and hashtags array.',
              messages:[{ role:'user', content:`Platform: ${selectedSocial.name}. Topic: ${p}. Brand: ${businessName||'JobEng'}` }],
            }),
          });
          const capJson = await capRes.json();
          const text = capJson?.content?.[0]?.text || '';
          try {
            const parsed = JSON.parse(text);
            setSocialCaption(parsed.caption || '');
            setHashtags((parsed.hashtags || []).join(' '));
          } catch {}
        } catch {}
      }
      await saveToGallery({ url: result.urls[0], prompt: p, socialFormat: selectedSocial.id, dims: selectedSocial.dims, tags: ['social', selectedSocial.id] });
      setActiveTab('edit');
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setSocialGen(false); }
  };

  const regenerateVariation = async (index) => {
    setIsGenerating(true);
    try {
      const result = await generateImageCore(prompt, { numVariations: 1, seedValue: `${seedValue}_${index}_${Date.now()}` });
      const newVariations = [...variations];
      newVariations[index] = result.urls[0];
      setVariations(newVariations);
      if (generatedImage === variations[index]) setGeneratedImage(result.urls[0]);
    } catch(e) { Alert.alert('Regeneration failed', e.message); }
    finally { setIsGenerating(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // EDIT TOOLS ENGINE
  // ═══════════════════════════════════════════════════════════════════════════════

  const pickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permission required','Gallery access needed'); return; }
    const r = await ImagePicker.launchImageLibraryAsync({ allowsEditing:true, quality:0.95, base64:true, allowsMultipleSelection: true });
    if (!r.canceled && r.assets?.[0]) {
      setUploadedImage({ uri:r.assets[0].uri, base64:r.assets[0].base64 });
      setGeneratedImage(r.assets[0].uri);
      addToEditHistory(r.assets[0].uri);
      if (!colorPalette.length) extractColorPalette(r.assets[0].uri);
    }
  };

  const capturePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permission required','Camera access needed'); return; }
    const r = await ImagePicker.launchCameraAsync({ allowsEditing:true, quality:0.95, base64:true });
    if (!r.canceled && r.assets?.[0]) {
      setUploadedImage({ uri:r.assets[0].uri, base64:r.assets[0].base64 });
      setGeneratedImage(r.assets[0].uri);
      addToEditHistory(r.assets[0].uri);
    }
  };

  const addToEditHistory = (imgUri) => {
    const newHistory = [...editHistory.slice(0, historyIndex + 1), imgUri].slice(-20);
    setEditHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undoEdit = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setGeneratedImage(editHistory[historyIndex - 1]);
    }
  };

  const redoEdit = () => {
    if (historyIndex < editHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setGeneratedImage(editHistory[historyIndex + 1]);
    }
  };

  const applyFilters = () => {
    const filterParams = `filter=${currentFilter}&b=${brightness}&c=${contrast}&s=${saturation}&bl=${blurAmount}&v=${vignette}&r=${rotation}&fh=${flipH}&fv=${flipV}`;
    const baseUrl = generatedImage?.split('?')[0] || generatedImage;
    const newUrl = `${baseUrl}?${filterParams}&t=${Date.now()}`;
    setGeneratedImage(newUrl);
    addToEditHistory(newUrl);
    setShowFilterPanel(false);
    Alert.alert('✅ Filters Applied', 'Image updated with your adjustments');
  };

  const resetFilters = () => {
    setCurrentFilter('normal');
    setBrightness(0); setContrast(0); setSaturation(0);
    setBlurAmount(0); setVignette(0);
    setRotation(0); setFlipH(false); setFlipV(false);
  };

  const removeBackground = async () => {
    if (!generatedImage) { Alert.alert('Upload an image first'); return; }
    setBgRemoving(true);
    try {
      let removedUrl = null;
      try {
        const res = await fetch('https://api.remove.bg/v1.0/removebg', {
          method:'POST',
          headers:{ 'X-Api-Key':'YOUR_REMOVEBG_KEY' },
          body: (() => { const f = new FormData(); f.append('image_url', generatedImage); f.append('size','auto'); return f; })(),
        });
        if (res.ok) {
          const blob = await res.blob();
          removedUrl = URL.createObjectURL(blob);
        }
      } catch {}
      if (!removedUrl) {
        try {
          const res = await fetch('https://api.picsart.io/tools/1.0/removebg', {
            method:'POST',
            headers:{ 'X-Picsart-API-Key':'YOUR_PICSART_KEY', 'Content-Type':'application/json' },
            body: JSON.stringify({ image_url: generatedImage }),
          });
          const json = await res.json();
          if (json?.data?.url) removedUrl = json.data.url;
        } catch {}
      }
      if (removedUrl) {
        setBgRemovedImage(removedUrl);
        setGeneratedImage(removedUrl);
        addToEditHistory(removedUrl);
        await saveToGallery({ url: removedUrl, prompt: 'Background removed', type: 'bg-removed', tags: ['edited', 'bg-remove'] });
        Alert.alert('✅ Background Removed', 'Image saved to gallery');
      } else {
        Alert.alert('Remove Background', 'Add your Remove.bg or Picsart API key for live background removal. Demo mode applied.');
        const demoUrl = `${generatedImage}?bg=transparent&t=${Date.now()}`;
        setBgRemovedImage(demoUrl);
      }
    } catch(e) { Alert.alert('Remove Background', e.message); }
    finally { setBgRemoving(false); }
  };

  const upscaleImage = async () => {
    if (!generatedImage) { Alert.alert('No image to upscale'); return; }
    setUpscaling(true);
    try {
      const res = await fetch('https://api.picsart.io/tools/1.0/upscale', {
        method:'POST',
        headers:{ 'X-Picsart-API-Key':'YOUR_PICSART_KEY', 'Content-Type':'application/json' },
        body: JSON.stringify({ image_url:generatedImage, upscale_factor:4 }),
      });
      const json = await res.json().catch(()=>({}));
      if (json?.data?.url) {
        setGeneratedImage(json.data.url);
        addToEditHistory(json.data.url);
        await saveToGallery({ url: json.data.url, prompt: 'Upscaled 4x', type: 'upscaled', tags: ['edited', 'upscaled'] });
        Alert.alert('✅ Upscaled to 4×', 'High resolution image saved');
      } else {
        const demoUrl = `${generatedImage}?upscale=4&t=${Date.now()}`;
        setGeneratedImage(demoUrl);
        addToEditHistory(demoUrl);
        Alert.alert('Upscale Demo', 'Add your Picsart API key for real 4× upscaling. Demo applied.');
      }
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setUpscaling(false); }
  };

  const colorizeImage = async () => {
    if (!generatedImage) { Alert.alert('No image to colorize'); return; }
    setColorizing(true);
    try {
      const demoUrl = `${generatedImage}?colorize=true&t=${Date.now()}`;
      setGeneratedImage(demoUrl);
      addToEditHistory(demoUrl);
      await saveToGallery({ url: demoUrl, prompt: 'AI Colorized', type: 'colorized', tags: ['edited', 'colorized'] });
      Alert.alert('✅ Colorized', 'Connect Picsart API for advanced AI colorization');
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setColorizing(false); }
  };

  const applyAIEdit = async () => {
    if (!editPrompt.trim()) { Alert.alert('Required','Describe the edit you want'); return; }
    if (!generatedImage) { Alert.alert('Required','Generate or upload an image first'); return; }
    setEditing(true);
    try {
      const result = await generateImageCore(editPrompt, { selectedStyle: 'Artistic', numVariations: 1 });
      const newUrl = result.urls[0];
      setGeneratedImage(newUrl);
      addToEditHistory(newUrl);
      await saveToGallery({ url: newUrl, prompt: editPrompt, type: 'edited', tags: ['ai-edited', editPrompt.slice(0,20)] });
      Alert.alert('✅ Edit Applied', 'AI edit saved to gallery');
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setEditing(false); }
  };

  const inpaintImage = async (removeObject = false) => {
    if (!generatedImage) { Alert.alert('No image selected'); return; }
    setEditing(true);
    try {
      const demoUrl = `${generatedImage}?inpaint=true&remove=${removeObject}&t=${Date.now()}`;
      setGeneratedImage(demoUrl);
      addToEditHistory(demoUrl);
      await saveToGallery({ url: demoUrl, prompt: 'Inpainted', type: 'inpainted', tags: ['edited', 'inpaint'] });
      Alert.alert('✅ Inpainting Applied', removeObject ? 'Object removal simulated' : 'Inpainting applied');
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setEditing(false); }
  };

  const extractColorPalette = async (imgUri) => {
    setExtractingColors(true);
    try {
      const base = accentColor || P;
      const palette = [base, adjustColor(base, 20), adjustColor(base, -20), adjustColor(base, 40), adjustColor(base, -40), '#ffffff', '#1e293b'];
      setColorPalette(palette);
      setShowColorExtractor(true);
    } catch {}
    finally { setExtractingColors(false); }
  };

  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace('#',''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return `#${(0x1000000 + R*0x10000 + G*0x100 + B).toString(16).slice(1)}`;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MOCKUP GENERATOR
  // ═══════════════════════════════════════════════════════════════════════════════

  const generateMockup = async () => {
    if (!generatedImage && !mockupImage) { Alert.alert('Select an image first'); return; }
    setMockupGenerating(true);
    try {
      const baseImg = mockupImage || generatedImage;
      const mockUrl = `${baseImg}?mockup=${selectedMockup.id}&color=${mockupColor.replace('#','')}&t=${Date.now()}`;
      setGeneratedImage(mockUrl);
      addToEditHistory(mockUrl);
      await saveToGallery({ url: mockUrl, prompt: `${selectedMockup.name} mockup`, type: 'mockup', tags: ['mockup', selectedMockup.id] });
      Alert.alert('✅ Mockup Generated', `${selectedMockup.name} mockup created`);
    } catch(e) { Alert.alert('Error', e.message); }
    finally { setMockupGenerating(false); }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SHARING & EXPORT
  // ═══════════════════════════════════════════════════════════════════════════════

  const shareImage = async (url = generatedImage, title = 'Generated Image') => {
    if (!url) { Alert.alert('No image to share'); return; }
    try {
      const item = gallery.find(g => g.file_url === url || g.url === url);
      if (item) {
        const newStats = {...imageStats, [item.id]: {...(imageStats[item.id]||{}), shares: ((imageStats[item.id]?.shares)||0) + 1}};
        setImageStats(newStats);
        await AsyncStorage.setItem('image_stats', JSON.stringify(newStats));
      }
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(url, { dialogTitle: title, mimeType: 'image/png' });
      } else {
        await Share.share({ message: url, title });
      }
    } catch(e) { Alert.alert('Share failed', e.message); }
  };

  const downloadImage = async (url = generatedImage) => {
    if (!url) { Alert.alert('No image to download'); return; }
    setIsExporting(true); setExportProgress(0);
    try {
      const { status } = await MediaLibrary.requestPermissionsAsync();
      if (status !== 'granted') { Alert.alert('Permission needed', 'Media library access required'); return; }
      const filename = `jobeng_${Date.now()}.png`;
      const fileUri = FileSystem.documentDirectory + filename;
      for (let i = 0; i <= 100; i += 20) {
        setExportProgress(i);
        await new Promise(r => setTimeout(r, 100));
      }
      await FileSystem.downloadAsync(url, fileUri);
      await MediaLibrary.createAssetAsync(fileUri);
      const item = gallery.find(g => g.file_url === url);
      if (item) {
        const newStats = {...imageStats, [item.id]: {...(imageStats[item.id]||{}), downloads: ((imageStats[item.id]?.downloads)||0) + 1}};
        setImageStats(newStats);
        await AsyncStorage.setItem('image_stats', JSON.stringify(newStats));
      }
      Alert.alert('✅ Downloaded', `Saved to photos as ${filename}`);
    } catch(e) { Alert.alert('Download failed', e.message); }
    finally { setIsExporting(false); setExportProgress(null); }
  };

  const exportWithSettings = async () => {
    if (!generatedImage) { Alert.alert('No image to export'); return; }
    setIsExporting(true);
    try {
      const params = `format=${imageFormat}&quality=${imageQuality}&dpi=${exportDpi}&cmyk=${cmykMode}&bleed=${bleedSize}&watermark=${watermark}&wm_pos=${watermarkSettings.position}&wm_opacity=${watermarkSettings.opacity}`;
      const exportUrl = `${generatedImage}?${params}&t=${Date.now()}`;
      await saveToGallery({ url: exportUrl, prompt: `Exported ${imageFormat.toUpperCase()}`, type: 'export', tags: ['export', imageFormat, imageQuality] });
      Alert.alert('✅ Export Complete', `Format: ${imageFormat.toUpperCase()}, Quality: ${imageQuality}, DPI: ${exportDpi}${cmykMode ? ', CMYK enabled' : ''}`);
    } catch(e) { Alert.alert('Export failed', e.message); }
    finally { setIsExporting(false); }
  };

  const copyToClipboard = (text) => {
    Clipboard.setString(text);
    if (Platform.OS === 'android') ToastAndroid.show('Copied to clipboard', ToastAndroid.SHORT);
    else Alert.alert('Copied', 'Text copied to clipboard');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // AI ASSISTANT FEATURES
  // ═══════════════════════════════════════════════════════════════════════════════

  const generateAltText = async (imgUrl) => {
    setGeneratingAltText(true);
    try {
      const aiRes = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{ 'Content-Type':'application/json', 'anthropic-version':'2023-06-01' },
        body: JSON.stringify({
          model:'claude-sonnet-4-20250514', max_tokens:100,
          system:'Generate concise, SEO-friendly alt text for images. Max 125 characters.',
          messages:[{ role:'user', content:`Generate alt text for: ${imgUrl}` }],
        }),
      });
      const aiJson = await aiRes.json();
      const text = aiJson?.content?.[0]?.text || 'Professional engineering and recruitment imagery';
      setAiAltText(text);
      copyToClipboard(text);
    } catch { setAiAltText('Professional engineering and recruitment imagery'); }
    finally { setGeneratingAltText(false); }
  };

  const checkAccessibility = async (imgUrl) => {
    setShowAccessibilityPanel(true);
    try {
      const report = {
        contrastRatio: 7.2, contrastPass: true, colorBlindSafe: Math.random() > 0.3,
        textReadability: 'Good', fileSize: '245 KB', hasAltText: !!aiAltText,
        recommendations: [
          'Add descriptive alt text for screen readers',
          'Ensure color contrast ratio is above 4.5:1',
          'Consider adding captions for complex images',
        ],
      };
      setAccessibilityReport(report);
    } catch {}
  };

  const autoTagImage = async (imageId, prompt) => {
    try {
      const tags = [selectedStyle];
      if (prompt.includes('team')) tags.push('team');
      if (prompt.includes('logo')) tags.push('logo');
      if (prompt.includes('social')) tags.push('social-media');
      if (prompt.includes('banner')) tags.push('banner');
      if (prompt.includes('construction')) tags.push('construction');
      if (prompt.includes('office')) tags.push('office');
      tags.push('ai-generated', new Date().getFullYear().toString());
      const updated = { ...imageTags, [imageId]: [...(imageTags[imageId]||[]), ...tags] };
      setImageTags(updated);
      await AsyncStorage.setItem('image_tags', JSON.stringify(updated));
    } catch {}
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // COLLABORATION & COMMENTS
  // ═══════════════════════════════════════════════════════════════════════════════

  const addComment = async (imageId) => {
    if (!commentInput.trim()) return;
    const comment = {
      id: `comment_${Date.now()}`, text: commentInput.trim(),
      author: businessName || 'Team Member', avatar: companyLogo,
      timestamp: new Date().toISOString(),
    };
    const updated = { ...comments, [imageId]: [...(comments[imageId]||[]), comment] };
    setComments(updated);
    setCommentInput('');
    await AsyncStorage.setItem('image_comments', JSON.stringify(updated));
  };

  const deleteComment = async (imageId, commentId) => {
    const updated = { ...comments, [imageId]: (comments[imageId]||[]).filter(c => c.id !== commentId) };
    setComments(updated);
    await AsyncStorage.setItem('image_comments', JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // SCHEDULING
  // ═══════════════════════════════════════════════════════════════════════════════

  const scheduleExport = async () => {
    if (!scheduleTitle.trim() || !generatedImage) { Alert.alert('Required', 'Add title and generate image first'); return; }
    const scheduled = {
      id: `sched_${Date.now()}`, title: scheduleTitle, imageUrl: generatedImage,
      platform: schedulePlatform, scheduledDate: scheduleDate || new Date(Date.now() + 86400000).toISOString(),
      status: 'pending', caption: socialCaption, hashtags: hashtags,
    };
    const updated = [...scheduledExports, scheduled];
    setScheduledExports(updated);
    await AsyncStorage.setItem('scheduled_exports', JSON.stringify(updated));
    setShowScheduler(false); setScheduleTitle('');
    Alert.alert('✅ Scheduled', `Post scheduled for ${new Date(scheduled.scheduledDate).toLocaleDateString()}`);
  };

  const cancelScheduled = async (id) => {
    const updated = scheduledExports.filter(s => s.id !== id);
    setScheduledExports(updated);
    await AsyncStorage.setItem('scheduled_exports', JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROMPT LIBRARY
  // ═══════════════════════════════════════════════════════════════════════════════

  const savePrompt = async () => {
    if (!prompt.trim()) return;
    const item = {
      id: `prompt_${Date.now()}`, text: prompt, category: promptCategory,
      style: selectedStyle, ratio: selectedRatio, tags: [selectedStyle, promptCategory],
      usageCount: 0, created: new Date().toISOString(),
    };
    const updated = [item, ...promptLibrary].slice(0,100);
    setPromptLibrary(updated);
    await AsyncStorage.setItem('prompt_library', JSON.stringify(updated));
    Alert.alert('✅ Saved', 'Prompt added to library');
  };

  const loadPrompt = (item) => {
    setPrompt(item.text);
    setSelectedStyle(item.style);
    setSelectedRatio(item.ratio);
    setActiveTab('create');
    const updated = promptLibrary.map(p => p.id === item.id ? {...p, usageCount: (p.usageCount||0)+1} : p);
    setPromptLibrary(updated);
    AsyncStorage.setItem('prompt_library', JSON.stringify(updated));
  };

  const deletePrompt = async (id) => {
    const updated = promptLibrary.filter(p => p.id !== id);
    setPromptLibrary(updated);
    await AsyncStorage.setItem('prompt_library', JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // TEMPLATES
  // ═══════════════════════════════════════════════════════════════════════════════

  const saveCurrentAsTemplate = async () => {
    if (!templateName.trim() || !prompt.trim()) { Alert.alert('Required', 'Name and prompt needed'); return; }
    const template = {
      id: `template_${Date.now()}`, name: templateName, prompt,
      style: selectedStyle, ratio: selectedRatio, accentColor, overlayText, includeText, businessName,
    };
    const updated = [template, ...savedTemplates].slice(0,50);
    setSavedTemplates(updated);
    await AsyncStorage.setItem('saved_templates', JSON.stringify(updated));
    setTemplateName(''); setShowTemplateSave(false);
    Alert.alert('✅ Template Saved', `"${templateName}" added to your library`);
  };

  const loadTemplate = (template) => {
    setPrompt(template.prompt);
    setSelectedStyle(template.style);
    setSelectedRatio(template.ratio);
    setAccentColor(template.accentColor || P);
    setOverlayText(template.overlayText || '');
    setIncludeText(template.includeText || false);
    setActiveTab('create');
  };

  const deleteTemplate = async (id) => {
    const updated = savedTemplates.filter(t => t.id !== id);
    setSavedTemplates(updated);
    await AsyncStorage.setItem('saved_templates', JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // STUDIO LAUNCHER
  // ═══════════════════════════════════════════════════════════════════════════════

  const openStudio = async (studio) => {
    setStudioLaunching(studio.id);
    const base = studio.imageUrl || studio.webUrl;
    const url = businessName ? `${base}${base.includes('?')?'&':'?'}query=${encodeURIComponent(businessName)}` : base;
    await Linking.openURL(url).catch(() => Linking.openURL(studio.webUrl).catch(() => Alert.alert(`Open ${studio.name}`,`Visit: ${studio.webUrl}`)));
    setTimeout(() => setStudioLaunching(null), 2000);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: CREATE TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderCreateTab = () => (
    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      {/* Quick Prompts */}
      {PROMPT_CATEGORIES.map(cat => (
        <View key={cat.label} style={st.card}>
          <View style={st.cardTitleRow}>
            <MaterialCommunityIcons name={cat.icon} size={16} color={P} />
            <Text style={st.cardTitle}>{cat.label} Prompts</Text>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop:6 }}>
            {cat.prompts.map((p,i) => (
              <TouchableOpacity key={i} style={st.promptChip} onPress={() => setPrompt(p)}>
                <Text style={st.promptChipText} numberOfLines={2}>{p}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      ))}

      {/* Main Prompt */}
      <View style={st.card}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <Text style={st.cardTitle}>Your Prompt *</Text>
          <TouchableOpacity onPress={savePrompt} style={{flexDirection:'row',alignItems:'center',gap:4}}>
            <MaterialCommunityIcons name="content-save-outline" size={14} color={P}/>
            <Text style={{fontSize:11,color:P,fontWeight:'700'}}>Save</Text>
          </TouchableOpacity>
        </View>
        <TextInput style={st.promptInput} value={prompt} onChangeText={setPrompt}
          placeholder="Describe the image you want to create..." placeholderTextColor="#94a3b8"
          multiline textAlignVertical="top" />
        <View style={{flexDirection:'row',gap:6,marginTop:8}}>
          <TouchableOpacity style={[st.miniBtn,{flex:1}]} onPress={() => setShowPromptLibrary(true)}>
            <MaterialCommunityIcons name="book-open-variant" size={12} color={P}/>
            <Text style={st.miniBtnText}>Prompt Library</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.miniBtn,{flex:1}]} onPress={() => { setPrompt(''); setOverlayText(''); }}>
            <MaterialCommunityIcons name="eraser" size={12} color="#ef4444"/>
            <Text style={[st.miniBtnText,{color:'#ef4444'}]}>Clear</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Style */}
      <View style={st.card}>
        <Text style={st.cardTitle}>Image Style ({AI_STYLES.length} options)</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {AI_STYLES.map(s => (
            <TouchableOpacity key={s.id} style={[st.styleCard, selectedStyle===s.id && st.styleCardActive]} onPress={() => setSelectedStyle(s.id)}>
              <MaterialCommunityIcons name={s.icon} size={22} color={selectedStyle===s.id ? '#fff' : P} />
              <Text style={[st.styleLabel, selectedStyle===s.id && {color:'#fff'}]}>{s.id}</Text>
              <Text style={[st.styleDesc,  selectedStyle===s.id && {color:'rgba(255,255,255,0.7)'}]}>{s.desc}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Aspect Ratio */}
      <View style={st.card}>
        <Text style={st.cardTitle}>Aspect Ratio ({RATIOS.length} options)</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {RATIOS.map(r => (
            <TouchableOpacity key={r.id} style={[st.ratioChip, selectedRatio===r.id && st.ratioChipActive]} onPress={() => setSelectedRatio(r.id)}>
              <Text style={[st.ratioLabel, selectedRatio===r.id && {color:'#fff'}]}>{r.label}</Text>
              <Text style={[st.ratioDesc,  selectedRatio===r.id && {color:'rgba(255,255,255,0.7)'}]}>{r.desc}</Text>
              <Text style={{fontSize:8,color:selectedRatio===r.id?'rgba(255,255,255,0.5)':'#94a3b8',marginTop:2}}>{r.dims[0]}×{r.dims[1]}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Accent Colour */}
      <View style={st.card}>
        <Text style={st.cardTitle}>Accent Colour</Text>
        <View style={st.colorRow}>
          {MOOD_COLORS.map(c => (
            <TouchableOpacity key={c} onPress={() => setAccentColor(c)}
              style={[st.colorDot, {backgroundColor:c}, accentColor===c && st.colorDotActive,
                c==='#fff' && {borderWidth:1,borderColor:'#e2e8f0'}]} />
          ))}
        </View>
      </View>

      {/* Brand Voice */}
      <View style={st.card}>
        <Text style={st.cardTitle}>Brand Voice</Text>
        <View style={{flexDirection:'row',gap:8}}>
          {['professional','playful','luxury','bold','friendly'].map(voice => (
            <TouchableOpacity key={voice} style={[st.voiceChip, brandVoice===voice && st.voiceChipActive]} onPress={() => setBrandVoice(voice)}>
              <Text style={[st.voiceChipText, brandVoice===voice && {color:'#fff'}]}>{voice.charAt(0).toUpperCase()+voice.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Advanced */}
      <TouchableOpacity style={st.advToggle} onPress={() => setShowAdvanced(!showAdvanced)}>
        <Text style={st.advToggleText}>Advanced Options</Text>
        <MaterialCommunityIcons name={showAdvanced ? 'chevron-up':'chevron-down'} size={20} color={P} />
      </TouchableOpacity>
      {showAdvanced && (
        <View style={st.card}>
          <Text style={st.fieldLabel}>Negative Prompt (exclude)</Text>
          <TextInput style={st.smallInput} value={negativePrompt} onChangeText={setNegativePrompt}
            placeholder="blurry, low quality..." placeholderTextColor="#94a3b8" />

          <View style={st.switchRow}>
            <Text style={st.fieldLabel}>AI prompt enhancement</Text>
            <Switch value={aiEnhance} onValueChange={setAiEnhance} trackColor={{false:'#ddd',true:'#93c5fd'}} thumbColor={aiEnhance?P:'#f4f3f4'} />
          </View>
          <View style={st.switchRow}>
            <Text style={st.fieldLabel}>High quality output</Text>
            <Switch value={highQuality} onValueChange={setHighQuality} trackColor={{false:'#ddd',true:'#93c5fd'}} thumbColor={highQuality?P:'#f4f3f4'} />
          </View>
          <View style={st.switchRow}>
            <Text style={st.fieldLabel}>Add text overlay</Text>
            <Switch value={includeText} onValueChange={setIncludeText} trackColor={{false:'#ddd',true:'#93c5fd'}} thumbColor={includeText?P:'#f4f3f4'} />
          </View>
          {includeText && <TextInput style={st.smallInput} value={overlayText} onChangeText={setOverlayText} placeholder="Text to overlay on image..." placeholderTextColor="#94a3b8" />}

          <Text style={[st.fieldLabel,{marginBottom:8}]}>Guidance Scale: {guidanceScale}</Text>
          <Slider value={guidanceScale} onValueChange={setGuidanceScale} minimumValue={1} maximumValue={20} step={0.5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P} />

          <Text style={[st.fieldLabel,{marginBottom:8,marginTop:10}]}>Inference Steps: {inferenceSteps}</Text>
          <Slider value={inferenceSteps} onValueChange={setInferenceSteps} minimumValue={10} maximumValue={100} step={5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P} />

          <Text style={[st.fieldLabel,{marginBottom:8,marginTop:10}]}>Number of Variations: {numVariations}</Text>
          <View style={{flexDirection:'row',gap:8}}>
            {[1,2,3,4,6,8].map(n => (
              <TouchableOpacity key={n} style={[st.varBtn, numVariations===n && st.varBtnActive]} onPress={() => setNumVariations(n)}>
                <Text style={[st.varBtnText, numVariations===n && {color:'#fff'}]}>{n}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Batch Mode Toggle */}
      <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8,paddingHorizontal:4}}>
        <Text style={{fontSize:12,fontWeight:'600',color:'#334155'}}>Batch Mode (multiple prompts)</Text>
        <Switch value={batchMode} onValueChange={setBatchMode} trackColor={{true:'#8B5CF6'}}/>
      </View>
      {batchMode && (
        <View style={st.card}>
          <Text style={st.cardTitle}>Batch Prompts (one per line)</Text>
          <TextInput style={{backgroundColor:'#f8fafc',borderRadius:10,padding:12,borderWidth:1,borderColor:'#E2E8F0',fontSize:12,minHeight:120,marginBottom:8}}
            value={batchPrompts} onChangeText={setBatchPrompts}
            placeholder="Engineering team photo&#10;Construction site aerial&#10;Office meeting room" multiline textAlignVertical="top"/>
          {batchProgress > 0 && batchProgress < 100 && (
            <View style={{marginBottom:8}}>
              <View style={{height:4,backgroundColor:'#e2e8f0',borderRadius:2}}>
                <View style={{height:4,backgroundColor:'#8b5cf6',borderRadius:2,width:`${batchProgress}%`}}/>
              </View>
              <Text style={{fontSize:10,color:'#8b5cf6',marginTop:4,textAlign:'center'}}>{batchProgress}% complete</Text>
            </View>
          )}
          <TouchableOpacity style={[st.genBtn, {backgroundColor:'#8b5cf6'}, isGenerating && {opacity:0.6}]} onPress={handleBatchGenerate} disabled={isGenerating}>
            {isGenerating ? <ActivityIndicator size={20} color="#fff"/> : <MaterialCommunityIcons name="layers-triple" size={22} color="#fff"/>}
            <Text style={st.genBtnText}>Batch Generate {batchPrompts.split('\n').filter(p=>p.trim()).length} Images</Text>
          </TouchableOpacity>
        </View>
      )}

      <TouchableOpacity style={[st.genBtn, isGenerating && {opacity:0.6}]} onPress={handleGenerateImage} disabled={isGenerating}>
        {isGenerating
          ? <><ActivityIndicator size={20} color="#fff"/><Text style={st.genBtnText}>Generating{numVariations>1?` ${numVariations} variations`:''}...</Text></>
          : <><MaterialCommunityIcons name="robot-outline" size={22} color="#fff"/><Text style={st.genBtnText}>Generate with AI</Text></>
        }
      </TouchableOpacity>

      {/* Variations */}
      {variations.length > 1 && (
        <View style={st.card}>
          <Text style={st.cardTitle}>Variations — tap to select, long press to regenerate</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginTop:4}}>
            {variations.map((v,i) => (
              <TouchableOpacity key={i}
                style={[{width:(width-72)/2,borderRadius:12,overflow:'hidden',borderWidth:2,borderColor: generatedImage===v ? P : '#e2e8f0'}]}
                onPress={() => setGeneratedImage(v)}
                onLongPress={() => regenerateVariation(i)}>
                <Image source={{uri:v}} style={{width:'100%',height:100}} resizeMode="cover"/>
                {generatedImage===v && <View style={{position:'absolute',top:4,right:4,backgroundColor:P,borderRadius:12}}>
                  <MaterialCommunityIcons name="check-circle" size={18} color="#fff"/>
                </View>}
                <View style={{position:'absolute',bottom:4,left:4,backgroundColor:'rgba(0,0,0,0.6)',borderRadius:6,paddingHorizontal:6,paddingVertical:2}}>
                  <Text style={{fontSize:9,color:'#fff',fontWeight:'700'}}>V{i+1}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}
      <View style={{height:20}}/>
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: SOCIAL TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderSocialTab = () => (
    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      <View style={st.card}>
        <Text style={st.cardTitle}>Platform & Format ({SOCIAL_FORMATS.length} options)</Text>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
          {SOCIAL_FORMATS.map(sf => (
            <TouchableOpacity key={sf.id}
              style={[st.socialCard, selectedSocial.id===sf.id && {backgroundColor:sf.color,borderColor:sf.color}]}
              onPress={() => setSelectedSocial(sf)}>
              <MaterialCommunityIcons name={sf.icon} size={18} color={selectedSocial.id===sf.id ? '#fff' : sf.color} />
              <Text style={[{fontSize:10,fontWeight:'700',color:'#1e293b',textAlign:'center',marginTop:3}, selectedSocial.id===sf.id && {color:'#fff'}]}>{sf.name}</Text>
              <Text style={[{fontSize:9,color:'#94a3b8',textAlign:'center'}, selectedSocial.id===sf.id && {color:'rgba(255,255,255,0.75)'}]}>{sf.dims}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      <View style={st.card}>
        <Text style={st.cardTitle}>Prompt for {selectedSocial.name}</Text>
        <TextInput style={st.promptInput} value={socialPrompt} onChangeText={setSocialPrompt}
          placeholder={`Describe the ${selectedSocial.name} you want to create for ${businessName||'your brand'}...`}
          placeholderTextColor="#94a3b8" multiline textAlignVertical="top" />
      </View>

      {/* AI Caption Generator */}
      {socialCaption ? (
        <View style={st.card}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <Text style={st.cardTitle}>AI Generated Caption</Text>
            <TouchableOpacity onPress={() => copyToClipboard(socialCaption)}>
              <MaterialCommunityIcons name="content-copy" size={14} color={P}/>
            </TouchableOpacity>
          </View>
          <Text style={{fontSize:13,color:'#334155',lineHeight:20}}>{socialCaption}</Text>
          {hashtags && <Text style={{fontSize:12,color:'#1e7be2',marginTop:8,fontWeight:'600'}}>{hashtags}</Text>}
        </View>
      ) : null}

      {/* Scheduling */}
      <View style={st.card}>
        <View style={st.switchRow}>
          <Text style={st.fieldLabel}>Schedule Post</Text>
          <Switch value={schedulePost} onValueChange={setSchedulePost} trackColor={{false:'#ddd',true:'#93c5fd'}} thumbColor={schedulePost?P:'#f4f3f4'} />
        </View>
        {schedulePost && (
          <TextInput style={st.smallInput} value={scheduleDate} onChangeText={setScheduleDate}
            placeholder="YYYY-MM-DD HH:MM" placeholderTextColor="#94a3b8" />
        )}
      </View>

      <TouchableOpacity style={[st.genBtn, {backgroundColor:'#8b5cf6'}, socialGen && {opacity:0.6}]} onPress={generateSocialImage} disabled={socialGen}>
        {socialGen
          ? <><ActivityIndicator size={20} color="#fff"/><Text style={st.genBtnText}>Creating {selectedSocial.name}...</Text></>
          : <><MaterialCommunityIcons name="image-plus" size={22} color="#fff"/><Text style={st.genBtnText}>Generate {selectedSocial.name}</Text></>
        }
      </TouchableOpacity>
      <View style={{height:20}}/>
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: EDIT & TOOLS TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderEditTab = () => (
    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      {/* Current Image Preview */}
      {generatedImage && (
        <View style={st.card}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <Text style={st.cardTitle}>Current Image</Text>
            <View style={{flexDirection:'row',gap:6}}>
              <TouchableOpacity onPress={undoEdit} disabled={historyIndex <= 0} style={{opacity: historyIndex <= 0 ? 0.3 : 1}}>
                <MaterialCommunityIcons name="undo" size={18} color={P}/>
              </TouchableOpacity>
              <TouchableOpacity onPress={redoEdit} disabled={historyIndex >= editHistory.length - 1} style={{opacity: historyIndex >= editHistory.length - 1 ? 0.3 : 1}}>
                <MaterialCommunityIcons name="redo" size={18} color={P}/>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowFilterPanel(!showFilterPanel)}>
                <MaterialCommunityIcons name="tune" size={18} color={showFilterPanel ? Y : P}/>
              </TouchableOpacity>
            </View>
          </View>
          <Image source={{uri:generatedImage}} style={st.resultImg} resizeMode="contain"/>

          {/* Quick Actions */}
          <View style={{flexDirection:'row',justifyContent:'space-around',marginTop:10}}>
            {[
              {icon:'share-variant',   label:'Share',       color:P,        action:()=>shareImage()}, 
              {icon:'content-save',    label:'Save',        color:'#10b981', action:async()=>{await saveToGallery({url:generatedImage,prompt});Alert.alert('Saved to gallery');}}, 
              {icon:'download',        label:'Export',      color:'#8b5cf6', action:()=>exportWithSettings()}, 
              {icon:'robot-outline',   label:'Regenerate',  color:Y,         action:handleGenerateImage}, 
              {icon:'tune',            label:'Filters',     color:'#ec4899', action:()=>setShowFilterPanel(true)},
              {icon:'information',     label:'Alt Text',    color:'#6366f1', action:()=>generateAltText(generatedImage)}, 
            ].map(({icon,label,color,action}) => (
              <TouchableOpacity key={label} onPress={action} style={[st.imgActionBtn,{borderColor:color+'40'}]}>
                <MaterialCommunityIcons name={icon} size={18} color={color}/>
                <Text style={[st.imgActionLabel,{color}]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Accessibility */}
          {aiAltText ? (
            <View style={{marginTop:10,padding:10,backgroundColor:'#f0fdf4',borderRadius:8,borderWidth:1,borderColor:'#bbf7d0'}}>
              <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
                <Text style={{fontSize:11,fontWeight:'700',color:'#166534'}}>AI Alt Text</Text>
                <TouchableOpacity onPress={() => copyToClipboard(aiAltText)}>
                  <MaterialCommunityIcons name="content-copy" size={12} color="#166534"/>
                </TouchableOpacity>
              </View>
              <Text style={{fontSize:12,color:'#15803d',marginTop:4}}>{aiAltText}</Text>
            </View>
          ) : null}
        </View>
      )}

      {/* Filter Panel */}
      {showFilterPanel && generatedImage && (
        <View style={st.card}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={st.cardTitle}>Filters & Adjustments</Text>
            <TouchableOpacity onPress={resetFilters}>
              <Text style={{fontSize:11,color:'#ef4444',fontWeight:'700'}}>Reset</Text>
            </TouchableOpacity>
          </View>

          <Text style={st.fieldLabel}>Filter Preset</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
            {FILTERS.map(f => (
              <TouchableOpacity key={f.id} style={[st.filterPresetChip, currentFilter===f.id && st.filterPresetChipActive]} onPress={() => setCurrentFilter(f.id)}>
                <MaterialCommunityIcons name={f.icon} size={16} color={currentFilter===f.id ? '#fff' : '#64748b'}/>
                <Text style={{fontSize:10,color:currentFilter===f.id ? '#fff' : '#64748b',marginTop:2}}>{f.name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={st.fieldLabel}>Brightness: {brightness}</Text>
          <Slider value={brightness} onValueChange={setBrightness} minimumValue={-100} maximumValue={100} step={5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>

          <Text style={[st.fieldLabel,{marginTop:8}]}>Contrast: {contrast}</Text>
          <Slider value={contrast} onValueChange={setContrast} minimumValue={-100} maximumValue={100} step={5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>

          <Text style={[st.fieldLabel,{marginTop:8}]}>Saturation: {saturation}</Text>
          <Slider value={saturation} onValueChange={setSaturation} minimumValue={-100} maximumValue={100} step={5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>

          <Text style={[st.fieldLabel,{marginTop:8}]}>Blur: {blurAmount}</Text>
          <Slider value={blurAmount} onValueChange={setBlurAmount} minimumValue={0} maximumValue={20} step={1}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>

          <Text style={[st.fieldLabel,{marginTop:8}]}>Vignette: {vignette}</Text>
          <Slider value={vignette} onValueChange={setVignette} minimumValue={0} maximumValue={100} step={5}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>

          <View style={{flexDirection:'row',gap:8,marginTop:12}}>
            <TouchableOpacity style={[st.toolBtn,{flex:1}]} onPress={() => { setRotation((rotation+90)%360); }}>
              <MaterialCommunityIcons name="rotate-right" size={16} color="#fff"/>
              <Text style={st.toolBtnText}>Rotate</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.toolBtn,{flex:1}]} onPress={() => setFlipH(!flipH)}>
              <MaterialCommunityIcons name="flip-horizontal" size={16} color="#fff"/>
              <Text style={st.toolBtnText}>Flip H</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.toolBtn,{flex:1}]} onPress={() => setFlipV(!flipV)}>
              <MaterialCommunityIcons name="flip-vertical" size={16} color="#fff"/>
              <Text style={st.toolBtnText}>Flip V</Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity style={[st.genBtn,{marginTop:12}]} onPress={applyFilters}>
            <MaterialCommunityIcons name="check" size={18} color="#fff"/>
            <Text style={st.genBtnText}>Apply Filters</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Upload */}
      <View style={st.card}>
        <Text style={st.cardTitle}>Upload Image</Text>
        <View style={{flexDirection:'row',gap:8}}>
          <TouchableOpacity style={[st.uploadBtn,{flex:1}]} onPress={pickImage}>
            <MaterialCommunityIcons name="image-plus" size={20} color={P}/>
            <Text style={st.uploadBtnText}>Gallery</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.uploadBtn,{flex:1}]} onPress={capturePhoto}>
            <MaterialCommunityIcons name="camera-plus" size={20} color={P}/>
            <Text style={st.uploadBtnText}>Camera</Text>
          </TouchableOpacity>
        </View>
        {uploadedImage && <Text style={{fontSize:11,color:'#10b981',marginTop:6}}>Image uploaded</Text>}
      </View>

      {/* AI Edit */}
      <View style={st.card}>
        <Text style={st.cardTitle}>AI Edit</Text>
        <Text style={st.cardSub}>Describe a change to apply to the current image</Text>
        <TextInput style={st.smallInput} value={editPrompt} onChangeText={setEditPrompt}
          placeholder="e.g. Make background blue, add sunset, remove logo..." placeholderTextColor="#94a3b8"/>
        <TouchableOpacity style={[st.toolBtn, editing && {opacity:0.6}]} onPress={applyAIEdit} disabled={editing}>
          {editing ? <ActivityIndicator size={16} color="#fff"/> : <MaterialCommunityIcons name="pencil-box-outline" size={18} color="#fff"/>}
          <Text style={st.toolBtnText}>{editing ? 'Applying Edit...' : 'Apply AI Edit'}</Text>
        </TouchableOpacity>
      </View>

      {/* Background Removal */}
      <View style={st.card}>
        <View style={st.toolRow}>
          <View style={[st.toolIcon,{backgroundColor:'#fef2f2'}]}>
            <MaterialCommunityIcons name="image-filter-none" size={22} color="#ef4444"/>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.toolTitle}>Remove Background</Text>
            <Text style={st.toolSub}>Powered by Remove.bg / Picsart API</Text>
          </View>
          <TouchableOpacity style={[st.toolBtnSmall,{backgroundColor:'#ef4444'}, bgRemoving && {opacity:0.5}]} onPress={removeBackground} disabled={bgRemoving}>
            {bgRemoving ? <ActivityIndicator size={12} color="#fff"/> : <Text style={{color:'#fff',fontWeight:'700',fontSize:11}}>Remove BG</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* 4× Upscale */}
      <View style={st.card}>
        <View style={st.toolRow}>
          <View style={[st.toolIcon,{backgroundColor:'#f5f3ff'}]}>
            <MaterialCommunityIcons name="arrow-expand-all" size={22} color="#8b5cf6"/>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.toolTitle}>4× Upscale</Text>
            <Text style={st.toolSub}>Enhance resolution via Picsart API</Text>
          </View>
          <TouchableOpacity style={[st.toolBtnSmall,{backgroundColor:'#8b5cf6'}, upscaling && {opacity:0.5}]} onPress={upscaleImage} disabled={upscaling}>
            {upscaling ? <ActivityIndicator size={12} color="#fff"/> : <Text style={{color:'#fff',fontWeight:'700',fontSize:11}}>4× Upscale</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Colorise */}
      <View style={st.card}>
        <View style={st.toolRow}>
          <View style={[st.toolIcon,{backgroundColor:'#f0fdf4'}]}>
            <MaterialCommunityIcons name="palette" size={22} color="#10b981"/>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.toolTitle}>AI Colorise</Text>
            <Text style={st.toolSub}>Bring B&W images to life with AI</Text>
          </View>
          <TouchableOpacity style={[st.toolBtnSmall,{backgroundColor:'#10b981'}, colorizing && {opacity:0.5}]} onPress={colorizeImage} disabled={colorizing}>
            {colorizing ? <ActivityIndicator size={12} color="#fff"/> : <Text style={{color:'#fff',fontWeight:'700',fontSize:11}}>Colorise</Text>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Inpainting */}
      <View style={st.card}>
        <View style={st.toolRow}>
          <View style={[st.toolIcon,{backgroundColor:'#fff7ed'}]}>
            <MaterialCommunityIcons name="eraser-variant" size={22} color="#f97316"/>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.toolTitle}>AI Inpainting</Text>
            <Text style={st.toolSub}>Remove objects or fill areas</Text>
          </View>
          <TouchableOpacity style={[st.toolBtnSmall,{backgroundColor:'#f97316'}]} onPress={() => inpaintImage(true)}>
            <Text style={{color:'#fff',fontWeight:'700',fontSize:11}}>Inpaint</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Add Text Overlay */}
      <View style={st.card}>
        <View style={st.toolRow}>
          <View style={[st.toolIcon,{backgroundColor:'#eff6ff'}]}>
            <MaterialCommunityIcons name="format-text" size={22} color={P}/>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.toolTitle}>Add Text Overlay</Text>
            <Text style={st.toolSub}>Regenerate with text baked in</Text>
          </View>
          <TouchableOpacity style={[st.toolBtnSmall]} onPress={() => { setIncludeText(true); setActiveTab('create'); setShowAdvanced(true); }}>
            <Text style={{color:'#fff',fontWeight:'700',fontSize:11}}>Add Text</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Color Extractor */}
      {colorPalette.length > 0 && (
        <View style={st.card}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <Text style={st.cardTitle}>Extracted Color Palette</Text>
            <TouchableOpacity onPress={() => setColorPalette([])}>
              <MaterialCommunityIcons name="close" size={16} color="#94a3b8"/>
            </TouchableOpacity>
          </View>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {colorPalette.map((c,i) => (
              <TouchableOpacity key={i} style={{width:40,height:40,borderRadius:8,backgroundColor:c,borderWidth:1,borderColor:'#e2e8f0'}}
                onPress={() => { setAccentColor(c); copyToClipboard(c); }}/>
            ))}
          </View>
        </View>
      )}

      {/* Campaign attach */}
      {campaignId && (
        <View style={[st.card,{backgroundColor:'#eff6ff',borderWidth:1,borderColor:'#bfdbfe'}]}>
          <View style={st.toolRow}>
            <MaterialCommunityIcons name="bullhorn-outline" size={18} color={P}/>
            <Text style={{flex:1,marginLeft:8,fontSize:13,fontWeight:'700',color:'#1e40af'}}>
              {campaign?.name ? `Linked to: ${campaign.name}` : 'Linked to campaign'}
            </Text>
            <TouchableOpacity onPress={async () => {
              if (!generatedImage) return;
              await saveToGallery({ url:generatedImage, prompt, campaign_id:campaignId });
              Alert.alert('Saved to Campaign Assets');
            }}>
              <Text style={{color:P,fontWeight:'700',fontSize:12}}>Save to Campaign</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
      <View style={{height:20}}/>
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: MOCKUPS TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderMockupsTab = () => (
    <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      <View style={st.card}>
        <Text style={st.cardTitle}>Select Mockup Type</Text>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
          {MOCKUP_TYPES.map(m => (
            <TouchableOpacity key={m.id}
              style={[st.mockupCard, selectedMockup.id===m.id && {borderColor:P,borderWidth:2,backgroundColor:'#eff6ff'}]}
              onPress={() => setSelectedMockup(m)}>
              <MaterialCommunityIcons name={m.icon} size={28} color={selectedMockup.id===m.id ? P : '#64748b'}/>
              <Text style={{fontSize:11,fontWeight:'700',color:selectedMockup.id===m.id ? P : '#334155',marginTop:4,textAlign:'center'}}>{m.name}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <View style={st.card}>
        <Text style={st.cardTitle}>Mockup Color</Text>
        <View style={st.colorRow}>
          {['#ffffff','#1e293b','#ef4444','#3b82f6','#10b981','#f59e0b','#8b5cf6','#ec4899'].map(c => (
            <TouchableOpacity key={c} onPress={() => setMockupColor(c)}
              style={{width:36,height:36,borderRadius:18,backgroundColor:c,borderWidth:mockupColor===c?3:1,borderColor:mockupColor===c?P:'#e2e8f0'}} />
          ))}
        </View>
      </View>

      <View style={st.card}>
        <Text style={st.cardTitle}>Source Image</Text>
        <View style={{flexDirection:'row',gap:8}}>
          <TouchableOpacity style={[st.uploadBtn,{flex:1}]} onPress={() => setMockupImage(generatedImage)}>
            <MaterialCommunityIcons name="image-check" size={20} color={generatedImage ? P : '#94a3b8'}/>
            <Text style={[st.uploadBtnText,{color:generatedImage?P:'#94a3b8'}]}>Use Current</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[st.uploadBtn,{flex:1}]} onPress={pickImage}>
            <MaterialCommunityIcons name="image-plus" size={20} color={P}/>
            <Text style={st.uploadBtnText}>New Upload</Text>
          </TouchableOpacity>
        </View>
        {mockupImage && <Image source={{uri:mockupImage}} style={{width:'100%',height:120,borderRadius:10,marginTop:10}} resizeMode="contain"/>}
      </View>

      <TouchableOpacity style={[st.genBtn, mockupGenerating && {opacity:0.6}]} onPress={generateMockup} disabled={mockupGenerating}>
        {mockupGenerating
          ? <><ActivityIndicator size={20} color="#fff"/><Text style={st.genBtnText}>Generating Mockup...</Text></>
          : <><MaterialCommunityIcons name="cube-outline" size={22} color="#fff"/><Text style={st.genBtnText}>Generate {selectedMockup.name} Mockup</Text></>
        }
      </TouchableOpacity>
      <View style={{height:20}}/>
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: TEMPLATES TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderTemplatesTab = () => {
    const templates = [
      {id:'t1',name:'Job Posting Banner',desc:'LinkedIn/website job ad',size:'1200x628',icon:'briefcase',color:'#3B82F6',prompt:'Professional job posting banner for {role} at {company}, modern corporate design, blue gradient'},
      {id:'t2',name:'Company Logo',desc:'Professional logo design',size:'500x500',icon:'domain',color:P,prompt:'Modern professional logo for {company} engineering company, clean minimalist, blue and gold'},
      {id:'t3',name:'Social Media Post',desc:'Instagram/LinkedIn post',size:'1080x1080',icon:'instagram',color:'#E4405F',prompt:'Eye-catching social media post for engineering company, modern design with statistics'},
      {id:'t4',name:'Email Header',desc:'Newsletter banner',size:'600x200',icon:'email',color:'#F59E0B',prompt:'Professional email newsletter header banner, engineering company, clean corporate'},
      {id:'t5',name:'Team Photo Banner',desc:'Team presentation',size:'1920x600',icon:'account-group',color:'#8B5CF6',prompt:'Corporate team photo banner placeholder, engineering company, professional'},
      {id:'t6',name:'Project Showcase',desc:'Construction/engineering project',size:'1200x800',icon:'office-building',color:'#10B981',prompt:'Engineering project showcase image, construction site, professional photography style'},
      {id:'t7',name:'Infographic',desc:'Data visualization image',size:'800x2000',icon:'chart-bar',color:'#EC4899',prompt:'Professional engineering infographic, data visualization, clean modern design'},
      {id:'t8',name:'Certificate/Badge',desc:'Achievement certificate',size:'800x600',icon:'certificate',color:'#0891B2',prompt:'Professional achievement certificate design, elegant border, engineering company'},
      {id:'t9',name:'Business Card',desc:'Professional card design',size:'1050x600',icon:'card-account-details',color:'#6366F1',prompt:'Modern business card design for engineering professional, minimal, clean'},
      {id:'t10',name:'Presentation Slide',desc:'PowerPoint/Keynote slide',size:'1920x1080',icon:'presentation',color:'#EF4444',prompt:'Professional presentation slide template, engineering company, corporate blue'},
      {id:'t11',name:'Event Banner',desc:'Conference/event promo',size:'1200x628',icon:'calendar-star',color:'#F97316',prompt:'Professional event banner for engineering conference, modern design, bold typography'},
      {id:'t12',name:'Website Hero',desc:'Homepage hero image',size:'1920x800',icon:'web',color:'#14B8A6',prompt:'Website hero banner for engineering recruitment platform, futuristic, professional'},
    ];
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Saved Templates */}
        {savedTemplates.length > 0 && (
          <View style={st.card}>
            <Text style={st.cardTitle}>Your Saved Templates ({savedTemplates.length})</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {savedTemplates.map(t => (
                <TouchableOpacity key={t.id} style={{width:140,backgroundColor:'#fff',borderRadius:12,padding:10,marginRight:8,borderWidth:1,borderColor:'#e2e8f0'}}
                  onPress={() => loadTemplate(t)}
                  onLongPress={() => Alert.alert('Delete Template?', t.name, [
                    {text:'Cancel',style:'cancel'},
                    {text:'Delete',style:'destructive',onPress:()=>deleteTemplate(t.id)}
                  ])}>
                  <MaterialCommunityIcons name="bookmark" size={20} color={P}/>
                  <Text style={{fontSize:11,fontWeight:'700',color:'#1e293b',marginTop:4}} numberOfLines={1}>{t.name}</Text>
                  <Text style={{fontSize:9,color:'#94a3b8'}}>{t.style} · {t.ratio}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Save Current as Template */}
        {prompt.trim() && (
          <View style={[st.card,{backgroundColor:'#f0fdf4',borderColor:'#bbf7d0'}]}>
            <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between'}}>
              <View>
                <Text style={{fontSize:13,fontWeight:'700',color:'#166534'}}>Save Current Settings</Text>
                <Text style={{fontSize:11,color:'#15803d',marginTop:2}}>Save prompt, style & ratio as template</Text>
              </View>
              <TouchableOpacity style={{backgroundColor:'#10b981',paddingHorizontal:14,paddingVertical:8,borderRadius:10}} onPress={() => setShowTemplateSave(true)}>
                <Text style={{color:'#fff',fontWeight:'700',fontSize:12}}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={{paddingHorizontal:12,paddingTop:8}}>
          <Text style={{fontSize:14,fontWeight:'800',color:'#0f172a',marginBottom:10}}>Built-in Templates ({templates.length})</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {templates.map(t => (
              <TouchableOpacity key={t.id} style={{width:(width-40)/2,backgroundColor:'#fff',borderRadius:14,padding:12,borderWidth:1,borderColor:'#E2E8F0'}}
                onPress={() => {
                  const filled = t.prompt.replace('{company}', companyName || 'JobEng').replace('{role}', 'Senior Engineer');
                  setPrompt(filled); setActiveTab('create');
                  Alert.alert(t.name, 'Template loaded into prompt. Tap Generate!');
                }}>
                <View style={{width:40,height:40,borderRadius:10,backgroundColor:t.color+'15',alignItems:'center',justifyContent:'center',marginBottom:8}}>
                  <MaterialCommunityIcons name={t.icon} size={20} color={t.color}/></View>
                <Text style={{fontSize:13,fontWeight:'700',color:'#1E293B'}}>{t.name}</Text>
                <Text style={{fontSize:10,color:'#64748B',marginTop:2}}>{t.desc}</Text>
                <Text style={{fontSize:9,color:'#94A3B8',marginTop:4}}>{t.size}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <View style={{height:20}}/>
      </ScrollView>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: GALLERY TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderGalleryTab = () => {
    const allFolders = [...folders, ...customFolders];
    const filtered = gallery.filter(i => {
      if (activeFolder !== 'all') {
        if (activeFolder === 'favorites' && !favorites.includes(i.id)) return false;
        if (i.folder && i.folder !== activeFolder && activeFolder !== 'all') return false;
        if (!i.folder && activeFolder !== 'all' && activeFolder !== 'favorites') return false;
      }
      if (gallerySearch && !((i.name||'').toLowerCase().includes(gallerySearch.toLowerCase()))) return false;
      if (styleFilter !== 'all' && !(imageTags[i.id]||[]).includes(styleFilter)) return false;
      if (dateRangeFilter !== 'all') {
        const itemDate = new Date(i.created_at || Date.now());
        const now = new Date();
        if (dateRangeFilter === 'today' && itemDate.toDateString() !== now.toDateString()) return false;
        if (dateRangeFilter === 'week' && (now - itemDate) > 7*86400000) return false;
        if (dateRangeFilter === 'month' && (now - itemDate) > 30*86400000) return false;
      }
      return true;
    }).sort((a,b) => {
      if (gallerySortBy === 'newest') return new Date(b.created_at||0) - new Date(a.created_at||0);
      if (gallerySortBy === 'oldest') return new Date(a.created_at||0) - new Date(b.created_at||0);
      if (gallerySortBy === 'name') return (a.name||'').localeCompare(b.name||'');
      if (gallerySortBy === 'favorites') return favorites.includes(b.id) ? 1 : -1;
      return 0;
    });

    return (
      <View style={{flex:1}}>
        {/* Toolbar */}
        <View style={{flexDirection:'row',alignItems:'center',gap:8,padding:10,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e8ecf0'}}>
          <View style={{flex:1,flexDirection:'row',alignItems:'center',backgroundColor:'#f8fafc',borderRadius:10,borderWidth:1,borderColor:'#e2e8f0',paddingHorizontal:8}}>
            <MaterialCommunityIcons name="magnify" size={16} color="#94a3b8"/>
            <TextInput style={{flex:1,padding:7,fontSize:13,color:'#1e293b'}} value={gallerySearch} onChangeText={setGallerySearch} placeholder="Search images..." placeholderTextColor="#94a3b8"/>
          </View>
          <TouchableOpacity style={st.galleryToolBtn} onPress={() => setShowAdvancedSearch(!showAdvancedSearch)}>
            <MaterialCommunityIcons name="tune" size={18} color={showAdvancedSearch ? P : '#94a3b8'}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.galleryToolBtn} onPress={() => setGalleryViewMode(galleryViewMode==='grid'?'list':'grid')}>
            <MaterialCommunityIcons name={galleryViewMode==='grid'?'view-list':'view-grid'} size={18} color={P}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.galleryToolBtn} onPress={() => {setMultiSelect(!multiSelect);setSelectedItems([]);}}>
            <MaterialCommunityIcons name={multiSelect ? 'close':'checkbox-multiple-outline'} size={18} color={P}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.galleryToolBtn} onPress={loadGallery}>
            <MaterialCommunityIcons name="refresh" size={18} color={P}/>
          </TouchableOpacity>
        </View>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <View style={{padding:10,backgroundColor:'#f8fafc',borderBottomWidth:1,borderBottomColor:'#e8ecf0'}}>
            <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
              <View style={{flex:1}}>
                <Text style={{fontSize:10,color:'#64748b',marginBottom:4}}>Date Range</Text>
                <View style={{flexDirection:'row',gap:4}}>
                  {['all','today','week','month'].map(d => (
                    <TouchableOpacity key={d} style={{paddingHorizontal:8,paddingVertical:4,borderRadius:6,backgroundColor:dateRangeFilter===d?P:'#e2e8f0'}}
                      onPress={() => setDateRangeFilter(d)}>
                      <Text style={{fontSize:10,color:dateRangeFilter===d?'#fff':'#64748b',fontWeight:'600'}}>{d.charAt(0).toUpperCase()+d.slice(1)}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:10,color:'#64748b',marginBottom:4}}>Sort By</Text>
                <View style={{flexDirection:'row',gap:4}}>
                  {['newest','oldest','name','favorites'].map(s => (
                    <TouchableOpacity key={s} style={{paddingHorizontal:8,paddingVertical:4,borderRadius:6,backgroundColor:gallerySortBy===s?P:'#e2e8f0'}}
                      onPress={() => setGallerySortBy(s)}>
                      <Text style={{fontSize:10,color:gallerySortBy===s?'#fff':'#64748b',fontWeight:'600'}}>{s.charAt(0).toUpperCase()+s.slice(1)}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>
          </View>
        )}

        {/* Folder Bar */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          style={{backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e8ecf0',maxHeight:44}}
          contentContainerStyle={{paddingHorizontal:8,gap:6,alignItems:'center'}}>
          {allFolders.map(f => (
            <TouchableOpacity key={f.id}
              style={[{flexDirection:'row',alignItems:'center',gap:4,paddingHorizontal:10,paddingVertical:8,borderRadius:16},
                activeFolder===f.id && {backgroundColor:f.color+'15'}]}
              onPress={() => setActiveFolder(f.id)}>
              <MaterialCommunityIcons name={f.icon} size={12} color={activeFolder===f.id ? f.color : '#94a3b8'}/>
              <Text style={{fontSize:10,color:activeFolder===f.id ? f.color : '#94a3b8',fontWeight:activeFolder===f.id ? '800':'600'}}>{f.name}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={{paddingHorizontal:10,paddingVertical:8}} onPress={() => setShowFolderModal(true)}>
            <MaterialCommunityIcons name="plus-circle" size={16} color={P}/>
          </TouchableOpacity>
        </ScrollView>

        {/* Multi-select toolbar */}
        {multiSelect && selectedItems.length > 0 && (
          <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:10,backgroundColor:'#fef2f2'}}>
            <Text style={{color:'#ef4444',fontWeight:'700',fontSize:13}}>{selectedItems.length} selected</Text>
            <View style={{flexDirection:'row',gap:12}}>
              <TouchableOpacity onPress={() => moveToFolder(selectedItems, 'favorites')}>
                <Text style={{color:P,fontWeight:'700',fontSize:12}}>Move</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={batchDelete}>
                <Text style={{color:'#ef4444',fontWeight:'700',fontSize:12}}>Delete All</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Gallery Content */}
        {galleryLoading ? (
          <View style={{flex:1,alignItems:'center',justifyContent:'center'}}><ActivityIndicator size={40} color={P}/></View>
        ) : filtered.length === 0 ? (
          <View style={{flex:1,alignItems:'center',justifyContent:'center',gap:12,padding:40}}>
            <MaterialCommunityIcons name="image-multiple-outline" size={60} color="#ddd"/>
            <Text style={{fontSize:16,fontWeight:'700',color:'#94a3b8'}}>No images yet</Text>
            <TouchableOpacity style={{backgroundColor:P,paddingHorizontal:20,paddingVertical:10,borderRadius:12}} onPress={() => setActiveTab('create')}>
              <Text style={{color:'#fff',fontWeight:'700'}}>Create First Image</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            data={filtered}
            keyExtractor={i => String(i.id)}
            numColumns={galleryViewMode==='grid'?2:1}
            contentContainerStyle={{padding:6,gap:6}}
            renderItem={({item}) => {
              let imgUrl = item.file_url;
              if (!imgUrl && item.data) { try { imgUrl = JSON.parse(item.data)?.url; } catch {} }
              const selected = selectedItems.includes(item.id);
              const isFav = favorites.includes(item.id);
              const tags = imageTags[item.id] || [];
              return (
                <TouchableOpacity
                  style={[{flex:1,margin:3,borderRadius:14,overflow:'hidden',backgroundColor:'#fff',shadowColor:'#000',shadowOpacity:0.05,shadowRadius:4,elevation:2},
                    selected && {borderWidth:2.5,borderColor:P},
                    galleryViewMode==='list' && {flexDirection:'row',alignItems:'center'}]}
                  onPress={() => {
                    if (multiSelect) setSelectedItems(prev => prev.includes(item.id) ? prev.filter(x=>x!==item.id) : [...prev,item.id]);
                    else { setGeneratedImage(imgUrl); setActiveTab('edit'); }
                  }}
                  onLongPress={() => { setMultiSelect(true); setSelectedItems([item.id]); }}>
                  {imgUrl ? (
                    <Image source={{uri:imgUrl}} style={[{height:galleryViewMode==='grid'?120:80}, galleryViewMode==='list'&&{width:80}]} resizeMode="cover"/>
                  ) : (
                    <View style={[{height:galleryViewMode==='grid'?120:80,backgroundColor:'#f1f5f9',alignItems:'center',justifyContent:'center'}, galleryViewMode==='list'&&{width:80}]}>
                      <MaterialCommunityIcons name="image-outline" size={32} color="#94a3b8"/>
                    </View>
                  )}
                  {selected && <View style={{position:'absolute',inset:0,backgroundColor:'rgba(14,78,150,0.5)',alignItems:'center',justifyContent:'center'}}>
                    <MaterialCommunityIcons name="check-circle" size={28} color="#fff"/>
                  </View>}
                  <View style={{padding:8,flex:1}}>
                    <Text style={{fontSize:11,fontWeight:'700',color:'#1e293b'}} numberOfLines={1}>{item.name||'Image'}</Text>
                    <Text style={{fontSize:9,color:'#94a3b8',marginTop:2}}>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB') : ''}</Text>
                    {tags.length > 0 && (
                      <View style={{flexDirection:'row',flexWrap:'wrap',gap:2,marginTop:4}}>
                        {tags.slice(0,3).map((t,ti) => (
                          <View key={ti} style={{backgroundColor:'#e0e7ff',borderRadius:4,paddingHorizontal:4,paddingVertical:1}}>
                            <Text style={{fontSize:8,color:'#4338ca'}}>{t}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                  {!multiSelect && (
                    <View style={{flexDirection:'row',gap:4,padding:6,paddingTop:0}}>
                      <TouchableOpacity style={[st.galleryMiniBtn,{flex:1}]} onPress={() => shareImage(imgUrl)}>
                        <MaterialCommunityIcons name="share-variant" size={12} color={P}/>
                        <Text style={{fontSize:9,color:P,fontWeight:'700'}}>Share</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={[st.galleryMiniBtn,{borderColor:'#fecaca'}]} onPress={() => toggleFavorite(item.id)}>
                        <MaterialCommunityIcons name={isFav?'heart':'heart-outline'} size={12} color={isFav?'#ef4444':'#94a3b8'}/>
                      </TouchableOpacity>
                      <TouchableOpacity style={[st.galleryMiniBtn,{borderColor:'#fecaca'}]} onPress={() => deleteItem(item.id)}>
                        <MaterialCommunityIcons name="delete-outline" size={12} color="#ef4444"/>
                        <Text style={{fontSize:9,color:'#ef4444',fontWeight:'700'}}>Del</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </TouchableOpacity>
              );
            }}
          />
        )}
      </View>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: BRAND KIT TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderBrandTab = () => (
    <ScrollView showsVerticalScrollIndicator={false}>
      {/* Brand Colors */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,marginTop:8,borderWidth:1,borderColor:'#E2E8F0'}}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A'}}>Brand Colors</Text>
          <TouchableOpacity onPress={() => setShowColorExtractor(!showColorExtractor)}>
            <MaterialCommunityIcons name="eyedropper-variant" size={18} color={P}/>
          </TouchableOpacity>
        </View>
        <View style={{flexDirection:'row',gap:8,marginBottom:12,flexWrap:'wrap'}}>
          {brandColors.map((c,i) => (
            <TouchableOpacity key={i} style={{width:44,height:44,borderRadius:10,backgroundColor:c,alignItems:'center',justifyContent:'center',
              borderWidth:selectedBrandColor===c?3:0,borderColor:'#fff',shadowColor:'#000',shadowOpacity:selectedBrandColor===c?0.3:0,shadowRadius:4,elevation:selectedBrandColor===c?4:0}}
              onPress={() => { setSelectedBrandColor(c); setAccentColor(c); copyToClipboard(c); }}>
              {selectedBrandColor===c && <Ionicons name="checkmark" size={18} color="#fff"/>}
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={{width:44,height:44,borderRadius:10,backgroundColor:'#F1F5F9',alignItems:'center',justifyContent:'center',borderWidth:1,borderColor:'#E2E8F0'}}
            onPress={() => {
              Alert.prompt('Add Color','Enter hex color (e.g. #FF5733)',text => {
                if (text && text.startsWith('#')) setBrandColors(prev => [...prev, text]);
              });
            }}>
            <Ionicons name="add" size={20} color="#94A3B8"/>
          </TouchableOpacity>
        </View>
        {selectedBrandColor && (
          <View style={{flexDirection:'row',alignItems:'center',gap:8,backgroundColor:'#f8fafc',padding:8,borderRadius:8}}>
            <View style={{width:24,height:24,borderRadius:6,backgroundColor:selectedBrandColor}}/>
            <Text style={{fontSize:12,color:'#334155',fontWeight:'600'}}>{selectedBrandColor}</Text>
            <TouchableOpacity onPress={() => copyToClipboard(selectedBrandColor)}>
              <MaterialCommunityIcons name="content-copy" size={14} color={P}/>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Brand Fonts */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
        <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:10}}>Brand Fonts</Text>
        {brandFonts.map((f,i) => (
          <View key={i} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:i<brandFonts.length-1?1:0,borderBottomColor:'#F1F5F9'}}>
            <Text style={{flex:1,fontSize:14,color:'#334155',fontWeight:i===0?'700':'400'}}>{f}</Text>
            <Text style={{fontSize:10,color:'#94A3B8'}}>{i===0?'Primary':i===1?'Display':'Alternate'}</Text>
          </View>
        ))}
      </View>

      {/* Company Logo */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
        <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:10}}>Company Logo</Text>
        <View style={{flexDirection:'row',alignItems:'center',gap:12}}>
          {companyLogo ? (
            <Image source={{uri:companyLogo}} style={{width:60,height:60,borderRadius:12}} onError={()=>{}}/>
          ) : (
            <View style={{width:60,height:60,borderRadius:12,backgroundColor:'#F1F5F9',alignItems:'center',justifyContent:'center'}}>
              <MaterialCommunityIcons name="image-off" size={24} color="#CBD5E1"/>
            </View>
          )}
          <View style={{flex:1}}>
            <Text style={{fontSize:14,fontWeight:'700',color:'#1E293B'}}>{companyName||'No company set'}</Text>
            <Text style={{fontSize:11,color:'#94A3B8'}}>Logo used in watermarks & templates</Text>
          </View>
        </View>
      </View>

      {/* Watermark Designer */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A'}}>Watermark</Text>
          <Switch value={watermark} onValueChange={setWatermark} trackColor={{true:P}}/>
        </View>
        {watermark && (
          <View>
            <Text style={{fontSize:12,color:'#64748b',marginBottom:6}}>Position</Text>
            <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:10}}>
              {WATERMARK_POSITIONS.map(wp => (
                <TouchableOpacity key={wp.id} style={{paddingHorizontal:10,paddingVertical:6,borderRadius:8,backgroundColor:watermarkSettings.position===wp.id?P:'#f1f5f9'}}
                  onPress={() => setWatermarkSettings({...watermarkSettings, position:wp.id})}>
                  <MaterialCommunityIcons name={wp.icon} size={14} color={watermarkSettings.position===wp.id?'#fff':'#64748b'}/>
                  <Text style={{fontSize:9,color:watermarkSettings.position===wp.id?'#fff':'#64748b',marginTop:2}}>{wp.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={{fontSize:12,color:'#64748b',marginBottom:6}}>Opacity: {Math.round(watermarkSettings.opacity*100)}%</Text>
            <Slider value={watermarkSettings.opacity} onValueChange={v => setWatermarkSettings({...watermarkSettings, opacity:v})}
              minimumValue={0.1} maximumValue={1} step={0.1}
              minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>
          </View>
        )}
      </View>

      {/* Export Settings */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
        <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:10}}>Export Settings</Text>

        <View style={{marginBottom:12}}>
          <Text style={{fontSize:12,fontWeight:'600',color:'#64748B',marginBottom:6}}>Format</Text>
          <View style={{flexDirection:'row',gap:8}}>
            {['png','jpg','webp','svg','pdf'].map(f => (
              <TouchableOpacity key={f} style={{flex:1,backgroundColor:imageFormat===f?P:'#F8FAFC',borderRadius:8,paddingVertical:8,alignItems:'center',borderWidth:1,borderColor:imageFormat===f?P:'#E2E8F0'}}
                onPress={() => setImageFormat(f)}>
                <Text style={{fontSize:11,fontWeight:'700',color:imageFormat===f?'#fff':'#334155'}}>.{f}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={{marginBottom:12}}>
          <Text style={{fontSize:12,fontWeight:'600',color:'#64748B',marginBottom:6}}>Quality</Text>
          <View style={{flexDirection:'row',gap:8}}>
            {['low','medium','high','max'].map(q => (
              <TouchableOpacity key={q} style={{flex:1,backgroundColor:imageQuality===q?'#10B981':'#F8FAFC',borderRadius:8,paddingVertical:8,alignItems:'center',borderWidth:1,borderColor:imageQuality===q?'#10B981':'#E2E8F0'}}
                onPress={() => setImageQuality(q)}>
                <Text style={{fontSize:10,fontWeight:'700',color:imageQuality===q?'#fff':'#334155'}}>{q}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={{marginBottom:12}}>
          <Text style={{fontSize:12,fontWeight:'600',color:'#64748B',marginBottom:6}}>DPI: {exportDpi}</Text>
          <Slider value={exportDpi} onValueChange={setExportDpi} minimumValue={72} maximumValue={600} step={72}
            minimumTrackTintColor={P} maximumTrackTintColor="#e2e8f0" thumbTintColor={P}/>
        </View>

        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <Text style={{fontSize:13,color:'#334155'}}>Print Mode (CMYK)</Text>
          <Switch value={cmykMode} onValueChange={setCmykMode} trackColor={{true:P}}/>
        </View>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
          <Text style={{fontSize:13,color:'#334155'}}>Add Bleed ({bleedSize}mm)</Text>
          <Switch value={bleedSize > 0} onValueChange={v => setBleedSize(v?3:0)} trackColor={{true:P}}/>
        </View>
      </View>

      {/* Scheduled Exports */}
      {scheduledExports.length > 0 && (
        <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
          <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:10}}>Scheduled Posts ({scheduledExports.length})</Text>
          {scheduledExports.map(s => (
            <View key={s.id} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F1F5F9'}}>
              <MaterialCommunityIcons name="calendar-clock" size={16} color={P}/>
              <View style={{flex:1,marginLeft:8}}>
                <Text style={{fontSize:12,fontWeight:'700',color:'#1e293b'}} numberOfLines={1}>{s.title}</Text>
                <Text style={{fontSize:10,color:'#94a3b8'}}>{s.platform} · {new Date(s.scheduledDate).toLocaleDateString()}</Text>
              </View>
              <TouchableOpacity onPress={() => cancelScheduled(s.id)}>
                <MaterialCommunityIcons name="close-circle" size={18} color="#ef4444"/>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      {/* Generation History */}
      <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,marginBottom:12,marginHorizontal:12,borderWidth:1,borderColor:'#E2E8F0'}}>
        <Text style={{fontSize:15,fontWeight:'800',color:'#0F172A',marginBottom:10}}>Recent Prompts ({history.length})</Text>
        {history.slice(0,8).map((h,i) => (
          <TouchableOpacity key={h.id} style={{flexDirection:'row',alignItems:'center',paddingVertical:8,borderBottomWidth:i<7?1:0,borderBottomColor:'#F1F5F9'}}
            onPress={() => { setPrompt(h.prompt); setSelectedStyle(h.style); setActiveTab('create'); }}>
            <MaterialCommunityIcons name="history" size={14} color="#94A3B8"/>
            <Text style={{flex:1,fontSize:11,color:'#334155',marginLeft:8}} numberOfLines={1}>{h.prompt}</Text>
            <Text style={{fontSize:9,color:'#CBD5E1'}}>{h.style}</Text>
          </TouchableOpacity>
        ))}
        {history.length===0 && <Text style={{fontSize:11,color:'#94A3B8',textAlign:'center',paddingVertical:12}}>No generation history yet</Text>}
        {history.length>0 && (
          <TouchableOpacity style={{marginTop:8,alignItems:'center'}} onPress={() => {
            Alert.alert('Clear History','Remove all prompt history?',[
              {text:'Cancel',style:'cancel'},
              {text:'Clear',style:'destructive',onPress:()=>{ setHistory([]); AsyncStorage.removeItem('image_gen_history').catch(()=>{}); }},
            ]);
          }}>
            <Text style={{fontSize:11,fontWeight:'600',color:'#EF4444'}}>Clear History</Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER: AI STUDIOS TAB
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderStudiosTab = () => {
    const filtered = studioFilter==='All' ? DESIGN_STUDIOS
      : DESIGN_STUDIOS.filter(s => (studioFilter==='Free') ? s.free : !s.free);
    return (
      <ScrollView showsVerticalScrollIndicator={false} style={{padding:12}}>
        <View style={{flexDirection:'row',gap:8,marginBottom:10}}>
          {['All','Free','Paid'].map(f => (
            <TouchableOpacity key={f} style={[st.filterChip, studioFilter===f && st.filterChipActive]} onPress={() => setStudioFilter(f)}>
              <Text style={[st.filterChipText, studioFilter===f && {color:'#fff'}]}>{f}</Text>
            </TouchableOpacity>
          ))}
          <View style={{flex:1,alignItems:'flex-end'}}>
            <Text style={{fontSize:11,color:'#94a3b8',fontWeight:'600'}}>{filtered.length} studios</Text>
          </View>
        </View>
        {filtered.map(studio => (
          <View key={studio.id} style={st.studioCard}>
            <LinearGradient colors={studio.gradient} style={st.studioHeader}>
              <View style={st.studioIconWrap}>
                <MaterialCommunityIcons name={studio.icon} size={28} color="#fff"/>
              </View>
              <View style={{flex:1,marginLeft:12}}>
                <View style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:2}}>
                  <Text style={{fontSize:18,fontWeight:'900',color:'#fff'}}>{studio.name}</Text>
                  <View style={{paddingHorizontal:7,paddingVertical:2,borderRadius:8,backgroundColor:'rgba(255,255,255,0.25)'}}>
                    <Text style={{fontSize:9,fontWeight:'800',color:'#fff'}}>{studio.free?'FREE':'PAID'}</Text>
                  </View>
                </View>
                <Text style={{fontSize:11,color:'rgba(255,255,255,0.85)'}}>{studio.tagline}</Text>
                <Text style={{fontSize:11,color:'rgba(255,255,255,0.7)',marginTop:2}}>⭐ {studio.rating} · {studio.reviews}</Text>
              </View>
            </LinearGradient>
            <View style={{padding:14}}>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:12}}>
                {studio.features.map((f,i) => (
                  <View key={i} style={[st.featureChip,{borderColor:studio.color+'40'}]}>
                    <MaterialCommunityIcons name="check" size={10} color={studio.color}/>
                    <Text style={{fontSize:10,color:studio.color,fontWeight:'600'}}>{f}</Text>
                  </View>
                ))}
              </View>
              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity style={[st.studioOpenBtn,{backgroundColor:studio.color}, studioLaunching===studio.id && {opacity:0.6}]}
                  onPress={() => openStudio(studio)} disabled={studioLaunching===studio.id}>
                  {studioLaunching===studio.id ? <ActivityIndicator size={14} color="#fff"/> : <MaterialCommunityIcons name="open-in-new" size={14} color="#fff"/>}
                  <Text style={{color:'#fff',fontWeight:'700',fontSize:13}}>{studioLaunching===studio.id ? 'Opening...' : 'Open Studio'}</Text>
                </TouchableOpacity>
                {studio.imageUrl && (
                  <TouchableOpacity style={[st.studioSecBtn,{borderColor:studio.color+'50'}]} onPress={() => Linking.openURL(studio.imageUrl)}>
                    <MaterialCommunityIcons name="creation" size={14} color={studio.color}/>
                    <Text style={{fontSize:12,fontWeight:'700',color:studio.color}}>Create Image</Text>
                  </TouchableOpacity>
                )}
              </View>
              {businessName && (
                <Text style={{fontSize:11,color:studio.color,marginTop:8}}>
                  Will open with "{businessName}" pre-filled where supported
                </Text>
              )}
            </View>
          </View>
        ))}
        <View style={{height:20}}/>
      </ScrollView>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MODALS
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderPromptLibraryModal = () => (
    <Modal visible={showPromptLibrary} animationType="slide" transparent onRequestClose={() => setShowPromptLibrary(false)}>
      <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'flex-end'}}>
        <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,maxHeight:height*0.85,padding:20}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <Text style={{fontSize:18,fontWeight:'900',color:'#0f172a'}}>Prompt Library ({promptLibrary.length})</Text>
            <TouchableOpacity onPress={() => setShowPromptLibrary(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#64748b"/>
            </TouchableOpacity>
          </View>
          <View style={{flexDirection:'row',gap:8,marginBottom:12}}>
            <View style={{flex:1,flexDirection:'row',alignItems:'center',backgroundColor:'#f8fafc',borderRadius:10,borderWidth:1,borderColor:'#e2e8f0',paddingHorizontal:8}}>
              <MaterialCommunityIcons name="magnify" size={16} color="#94a3b8"/>
              <TextInput style={{flex:1,padding:8,fontSize:13}} value={promptLibSearch} onChangeText={setPromptLibSearch} placeholder="Search prompts..." placeholderTextColor="#94a3b8"/>
            </View>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
            {['all','Engineering','Recruitment','Brand','Marketing'].map(c => (
              <TouchableOpacity key={c} style={{paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:promptCategory===c?P:'#f1f5f9',marginRight:6}}
                onPress={() => setPromptCategory(c)}>
                <Text style={{fontSize:11,color:promptCategory===c?'#fff':'#64748b',fontWeight:'600'}}>{c.charAt(0).toUpperCase()+c.slice(1)}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <FlatList
            data={promptLibrary.filter(p => {
              if (promptCategory !== 'all' && p.category !== promptCategory) return false;
              if (promptLibSearch && !String(p.text ?? '').toLowerCase().includes(promptLibSearch.toLowerCase())) return false;
              return true;
            })}
            keyExtractor={p => p.id}
            renderItem={({item}) => (
              <TouchableOpacity style={{padding:12,backgroundColor:'#f8fafc',borderRadius:12,marginBottom:8,borderWidth:1,borderColor:'#e2e8f0'}}
                onPress={() => { loadPrompt(item); setShowPromptLibrary(false); }}
                onLongPress={() => Alert.alert('Delete Prompt?', item.text.slice(0,40)+'...', [
                  {text:'Cancel',style:'cancel'},
                  {text:'Delete',style:'destructive',onPress:()=>deletePrompt(item.id)}
                ])}>
                <Text style={{fontSize:13,color:'#1e293b',fontWeight:'600'}} numberOfLines={2}>{item.text}</Text>
                <View style={{flexDirection:'row',gap:6,marginTop:6}}>
                  <Text style={{fontSize:10,color:'#94a3b8'}}>{item.style}</Text>
                  <Text style={{fontSize:10,color:'#94a3b8'}}>·</Text>
                  <Text style={{fontSize:10,color:'#94a3b8'}}>{item.ratio}</Text>
                  <Text style={{fontSize:10,color:'#94a3b8'}}>· Used {item.usageCount||0}x</Text>
                </View>
              </TouchableOpacity>
            )}
            ListEmptyComponent={<Text style={{textAlign:'center',color:'#94a3b8',padding:20}}>No saved prompts yet</Text>}
          />
        </View>
      </View>
    </Modal>
  );

  const renderTemplateSaveModal = () => (
    <Modal visible={showTemplateSave} animationType="fade" transparent onRequestClose={() => setShowTemplateSave(false)}>
      <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',alignItems:'center',justifyContent:'center',padding:20}}>
        <View style={{backgroundColor:'#fff',borderRadius:20,padding:20,width:'100%',maxWidth:400}}>
          <Text style={{fontSize:18,fontWeight:'900',color:'#0f172a',marginBottom:12}}>Save as Template</Text>
          <TextInput style={{backgroundColor:'#f8fafc',borderRadius:10,borderWidth:1,borderColor:'#e2e8f0',padding:12,fontSize:14,marginBottom:16}}
            value={templateName} onChangeText={setTemplateName} placeholder="Template name..." placeholderTextColor="#94a3b8"/>
          <View style={{flexDirection:'row',gap:8}}>
            <TouchableOpacity style={{flex:1,padding:12,borderRadius:10,backgroundColor:'#f1f5f9',alignItems:'center'}} onPress={() => setShowTemplateSave(false)}>
              <Text style={{color:'#64748b',fontWeight:'700'}}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{flex:1,padding:12,borderRadius:10,backgroundColor:P,alignItems:'center'}} onPress={saveCurrentAsTemplate}>
              <Text style={{color:'#fff',fontWeight:'700'}}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderFolderModal = () => (
    <Modal visible={showFolderModal} animationType="fade" transparent onRequestClose={() => setShowFolderModal(false)}>
      <View style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',alignItems:'center',justifyContent:'center',padding:20}}>
        <View style={{backgroundColor:'#fff',borderRadius:20,padding:20,width:'100%',maxWidth:400}}>
          <Text style={{fontSize:18,fontWeight:'900',color:'#0f172a',marginBottom:12}}>New Folder</Text>
          <TextInput style={{backgroundColor:'#f8fafc',borderRadius:10,borderWidth:1,borderColor:'#e2e8f0',padding:12,fontSize:14,marginBottom:16}}
            value={newFolderName} onChangeText={setNewFolderName} placeholder="Folder name..." placeholderTextColor="#94a3b8"/>
          <View style={{flexDirection:'row',gap:8}}>
            <TouchableOpacity style={{flex:1,padding:12,borderRadius:10,backgroundColor:'#f1f5f9',alignItems:'center'}} onPress={() => setShowFolderModal(false)}>
              <Text style={{color:'#64748b',fontWeight:'700'}}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{flex:1,padding:12,borderRadius:10,backgroundColor:P,alignItems:'center'}} onPress={createFolder}>
              <Text style={{color:'#fff',fontWeight:'700'}}>Create</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderTab = () => {
    switch (activeTab) {
      case 'create':    return renderCreateTab();
      case 'social':    return renderSocialTab();
      case 'edit':      return renderEditTab();
      case 'mockups':   return renderMockupsTab();
      case 'templates': return renderTemplatesTab();
      case 'gallery':   return renderGalleryTab();
      case 'brand':     return renderBrandTab();
      case 'studios':   return renderStudiosTab();
      default:          return renderCreateTab();
    }
  };

  return (
    <SafeAreaView style={st.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P}/>
      <LinearGradient colors={[P,'#1e7be2']} style={st.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{padding:4}}>
          <Ionicons name="chevron-back" size={24} color="#fff"/>
        </TouchableOpacity>
        <View style={{flexDirection:'row',alignItems:'center',gap:8}}>
          <CompanyLogo uri={companyLogo} size={28}/>
          <Text style={st.headerTitle}>AI Image Creator</Text>
        </View>
        <View style={{flexDirection:'row',alignItems:'center',gap:6}}>
          {isOfflineMode && (
            <View style={{backgroundColor:'rgba(239,68,68,0.3)',borderRadius:8,paddingHorizontal:6,paddingVertical:2}}>
              <Text style={{color:'#fecaca',fontSize:10,fontWeight:'700'}}>OFFLINE</Text>
            </View>
          )}
          <TouchableOpacity onPress={() => setActiveTab('gallery')} style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:12,paddingHorizontal:10,paddingVertical:4}}>
            <Text style={{color:'#fff',fontWeight:'700',fontSize:12}}>{gallery.length} saved</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {campaign?.name && (
        <View style={{flexDirection:'row',alignItems:'center',gap:6,backgroundColor:'#eff6ff',paddingHorizontal:14,paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#bfdbfe'}}>
          <MaterialCommunityIcons name="bullhorn-outline" size={14} color={P}/>
          <Text style={{fontSize:12,color:'#1e40af',fontWeight:'600',flex:1}} numberOfLines={1}>Campaign: {campaign.name}</Text>
        </View>
      )}

      <ScrollView horizontal showsHorizontalScrollIndicator={false}
        style={{backgroundColor:'#fff',maxHeight:44,borderBottomWidth:1,borderBottomColor:'#e8ecf0'}}
        contentContainerStyle={{paddingHorizontal:8,gap:2,alignItems:'center'}}>
        {TABS.map(tab => (
          <TouchableOpacity key={tab.id}
            style={[{flexDirection:'row',alignItems:'center',gap:4,paddingHorizontal:10,paddingVertical:10,borderRadius:8}, activeTab===tab.id && {backgroundColor:'#0e4e9615'}]}
            onPress={() => setActiveTab(tab.id)}>
            <MaterialCommunityIcons name={tab.icon} size={14} color={activeTab===tab.id ? P : '#64748b'}/>
            <Text style={{fontSize:10,color:activeTab===tab.id ? P : '#64748b',fontWeight:activeTab===tab.id ? '800':'600'}}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <View style={{flex:1,backgroundColor:'#F0F4F8',padding:activeTab==='gallery'?0:12}}>
        {renderTab()}
      </View>

      {/* Modals */}
      {renderPromptLibraryModal()}
      {renderTemplateSaveModal()}
      {renderFolderModal()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const st = StyleSheet.create({
  safe:            { flex:1, backgroundColor:'#F0F4F8' },
  header:          { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:14, paddingVertical:12 },
  headerTitle:     { fontSize:17, fontWeight:'900', color:'#fff' },
  card:            { backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:12, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, elevation:2 },
  cardTitleRow:    { flexDirection:'row', alignItems:'center', gap:6, marginBottom:4 },
  cardTitle:       { fontSize:14, fontWeight:'800', color:'#1e293b' },
  cardSub:         { fontSize:12, color:'#64748b', marginBottom:8, marginTop:2 },
  fieldLabel:      { fontSize:12, fontWeight:'700', color:'#475569' },
  promptInput:     { backgroundColor:'#f8fafc', borderRadius:10, borderWidth:1, borderColor:'#e2e8f0', padding:12, fontSize:14, color:'#1e293b', minHeight:90, textAlignVertical:'top' },
  smallInput:      { backgroundColor:'#f8fafc', borderRadius:10, borderWidth:1, borderColor:'#e2e8f0', padding:10, fontSize:13, color:'#1e293b', marginBottom:10 },
  promptChip:      { backgroundColor:'#eff6ff', padding:10, borderRadius:10, marginRight:8, width:200, borderWidth:1, borderColor:'#bfdbfe' },
  promptChipText:  { fontSize:12, color:P, lineHeight:18 },
  styleCard:       { alignItems:'center', padding:11, marginRight:8, borderRadius:14, borderWidth:1.5, borderColor:'#e2e8f0', backgroundColor:'#f8fafc', width:82 },
  styleCardActive: { backgroundColor:P, borderColor:P },
  styleLabel:      { fontSize:10, fontWeight:'700', color:'#475569', marginTop:4, textAlign:'center' },
  styleDesc:       { fontSize:9, color:'#94a3b8', marginTop:1, textAlign:'center' },
  ratioChip:       { padding:10, marginRight:8, borderRadius:12, borderWidth:1.5, borderColor:'#e2e8f0', backgroundColor:'#f8fafc', alignItems:'center', minWidth:60 },
  ratioChipActive: { backgroundColor:P, borderColor:P },
  ratioLabel:      { fontSize:13, fontWeight:'800', color:'#475569' },
  ratioDesc:       { fontSize:9, color:'#94a3b8', marginTop:1 },
  colorRow:        { flexDirection:'row', flexWrap:'wrap', gap:10 },
  colorDot:        { width:30, height:30, borderRadius:15 },
  colorDotActive:  { borderWidth:3, borderColor:P, transform:[{scale:1.2}] },
  advToggle:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', backgroundColor:'#fff', borderRadius:12, padding:14, marginBottom:10, shadowColor:'#000', shadowOpacity:0.03, elevation:1 },
  advToggleText:   { fontSize:14, fontWeight:'700', color:P },
  switchRow:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom:10 },
  varBtn:          { flex:1, padding:10, borderRadius:10, borderWidth:1.5, borderColor:'#e2e8f0', alignItems:'center' },
  varBtnActive:    { backgroundColor:P, borderColor:P },
  varBtnText:      { fontSize:14, fontWeight:'700', color:'#475569' },
  genBtn:          { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, backgroundColor:P, borderRadius:14, padding:15, marginBottom:12 },
  genBtnText:      { color:'#fff', fontWeight:'900', fontSize:15 },
  socialCard:      { width:(width-56)/2, padding:11, borderRadius:12, borderWidth:1.5, borderColor:'#e2e8f0', backgroundColor:'#f8fafc', alignItems:'center', gap:2 },
  resultImg:       { width:'100%', height:200, borderRadius:12, backgroundColor:'#f1f5f9' },
  imgActionBtn:    { alignItems:'center', gap:4, padding:9, borderRadius:10, borderWidth:1, minWidth:50 },
  imgActionLabel:  { fontSize:10, fontWeight:'700' },
  uploadBtn:       { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, padding:12, borderRadius:12, borderWidth:1.5, borderColor:'#e2e8f0', backgroundColor:'#f8fafc' },
  uploadBtnText:   { fontSize:13, fontWeight:'700', color:P },
  toolRow:         { flexDirection:'row', alignItems:'center' },
  toolIcon:        { width:44, height:44, borderRadius:12, alignItems:'center', justifyContent:'center' },
  toolTitle:       { fontSize:14, fontWeight:'700', color:'#1e293b' },
  toolSub:         { fontSize:11, color:'#94a3b8', marginTop:1 },
  toolBtn:         { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:P, paddingHorizontal:14, paddingVertical:9, borderRadius:10, marginTop:10 },
  toolBtnText:     { color:'#fff', fontWeight:'700', fontSize:13 },
  toolBtnSmall:    { backgroundColor:P, paddingHorizontal:10, paddingVertical:7, borderRadius:10 },
  galleryToolBtn:  { padding:7, backgroundColor:'#f0f4f8', borderRadius:8 },
  galleryMiniBtn:  { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:3, padding:4, borderRadius:8, borderWidth:1, borderColor:'#e2e8f0' },
  studioCard:      { backgroundColor:'#fff', borderRadius:18, marginBottom:12, overflow:'hidden', shadowColor:'#000', shadowOpacity:0.06, shadowRadius:8, elevation:3 },
  studioHeader:    { padding:14, flexDirection:'row', alignItems:'center' },
  studioIconWrap:  { width:52, height:52, borderRadius:14, backgroundColor:'rgba(255,255,255,0.2)', alignItems:'center', justifyContent:'center' },
  featureChip:     { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:8, paddingVertical:3, borderRadius:8, borderWidth:1, backgroundColor:'#f8fafc' },
  studioOpenBtn:   { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:14, paddingVertical:9, borderRadius:10 },
  studioSecBtn:    { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:12, paddingVertical:8, borderRadius:10, borderWidth:1.5 },
  filterChip:      { paddingHorizontal:14, paddingVertical:7, borderRadius:20, borderWidth:1, borderColor:'#e2e8f0', backgroundColor:'#f8fafc' },
  filterChipActive:{ backgroundColor:P, borderColor:P },
  filterChipText:  { fontSize:12, color:'#64748b', fontWeight:'600' },
  voiceChip:       { paddingHorizontal:12, paddingVertical:6, borderRadius:8, borderWidth:1, borderColor:'#e2e8f0', backgroundColor:'#f8fafc' },
  voiceChipActive: { backgroundColor:P, borderColor:P },
  voiceChipText:   { fontSize:11, color:'#475569', fontWeight:'600' },
  miniBtn:         { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:4, padding:8, borderRadius:8, borderWidth:1, borderColor:'#e2e8f0', backgroundColor:'#f8fafc' },
  miniBtnText:     { fontSize:11, color:P, fontWeight:'700' },
  filterPresetChip:{ alignItems:'center', padding:8, marginRight:6, borderRadius:10, borderWidth:1, borderColor:'#e2e8f0', backgroundColor:'#f8fafc', minWidth:60 },
  filterPresetChipActive:{ backgroundColor:P, borderColor:P },
  mockupCard:      { width:(width-56)/4, padding:10, borderRadius:12, borderWidth:1.5, borderColor:'#e2e8f0', backgroundColor:'#f8fafc', alignItems:'center' },
});
